import React, { useState, useEffect } from 'react';
import { apiCall } from '../config';
import { getStationName, getStationId } from '../utils/stationMapping';
import { useTranslation } from '../i18n/LanguageContext';

// Funktion zur Datumsformatierung von yyyy-mm-dd zu dd-mm-yyyy
const formatDateForDisplay = (dateString) => {
  if (!dateString) return '';
  const parts = dateString.split('-');
  if (parts.length === 3) {
    return `${parts[2]}-${parts[1]}-${parts[0]}`;
  }
  return dateString;
};

// Sichere Funktion für Backend-Stundenvalidierung mit intelligenter Mitternachts-Korrektur
const validateAndCorrectHours = (backendHours, context = '') => {
  if (typeof backendHours !== 'number') {
    const parsed = parseFloat(backendHours);
    if (isNaN(parsed)) {
      console.warn(`⚠️ Invalid hours value in EmployeeWorkReports:`, { backendHours, context });
      return 0;
    }
    backendHours = parsed;
  }
  
  // Intelligente Korrektur für negative Stunden (Backend-Mitternachts-Problem)
  if (backendHours < 0) {
    // Für Mitternachts-überschreitende Schichten: Korrigiere negative Stunden
    // Wenn Backend -16h berechnet hat (1-17=-16), dann sind das eigentlich 8h (24+(-16)=8)
    const correctedHours = 24 + backendHours;
    
    // Nur korrigieren wenn das Ergebnis sinnvoll ist (zwischen 1 und 23 Stunden)
    if (correctedHours > 0 && correctedHours <= 23) {
      console.info(`🔄 EmployeeWorkReports: Correcting midnight shift from backend:`, {
        originalValue: backendHours,
        correctedValue: correctedHours,
        context,
        explanation: `Detected midnight shift: ${backendHours}h corrected to ${correctedHours}h`
      });
      return correctedHours;
    } else {
      // Falls die Korrektur nicht sinnvoll ist, setze auf 0
      console.warn(`⚠️ EmployeeWorkReports: Cannot correct negative hours, setting to 0:`, {
        originalValue: backendHours,
        attemptedCorrection: correctedHours,
        context
      });
      return 0;
    }
  }
  
  // Prüfe auf unrealistisch hohe Werte
  if (backendHours > 24) {
    console.warn(`⚠️ EmployeeWorkReports: Unusually high hours detected:`, {
      value: backendHours,
      context
    });
  }
  
  return backendHours;
};

// Erweiterte Aggregierungs-Korrektur für Backend-Summen
const correctAggregatedHours = (employeeLogs, hourFieldName) => {
  // Berechne sowohl die naive Korrektur als auch eine erweiterte Schätzung
  const naiveSum = safeSumBackendHours(employeeLogs, hourFieldName);
  
  // Erweiterte Korrektur: Analysiere negative Werte und schätze korrekte Summe
  let extendedCorrectionSum = 0;
  let negativeEntries = 0;
  
  employeeLogs.forEach(log => {
    const originalValue = parseFloat(log[hourFieldName]) || 0;
    
    if (originalValue < 0) {
      negativeEntries++;
      // Für stark negative Werte (< -10), schätze mehrere Mitternachts-Schichten
      if (originalValue < -10) {
        // Schätze: Pro 8h-Nachtschicht wird backend ca. -16h berechnen
        const estimatedShifts = Math.abs(originalValue) / 16;
        const estimatedHours = estimatedShifts * 8;
        extendedCorrectionSum += estimatedHours;
        
        console.info(`📊 Extended correction estimate for ${log.firstname} ${log.lastname}:`, {
          originalSum: originalValue,
          estimatedShifts: estimatedShifts.toFixed(1),
          estimatedHours: estimatedHours.toFixed(1)
        });
      } else {
        // Normale Mitternachts-Korrektur
        extendedCorrectionSum += Math.max(0, 24 + originalValue);
      }
    } else {
      extendedCorrectionSum += originalValue;
    }
  });
  
  // Wenn es signifikante Unterschiede gibt, verwende die erweiterte Korrektur
  if (negativeEntries > 0 && Math.abs(extendedCorrectionSum - naiveSum) > 5) {
    console.warn(`🔧 Using extended correction for ${hourFieldName}:`, {
      naiveSum: naiveSum.toFixed(2),
      extendedSum: extendedCorrectionSum.toFixed(2),
      negativeEntries,
      difference: (extendedCorrectionSum - naiveSum).toFixed(2)
    });
    return extendedCorrectionSum;
  }
  
  return naiveSum;
};

// Funktion zur Berechnung der Ist-Stunden für einen benutzerdefinierten Zeitraum
const calculateCustomRangeHours = async (employeeId, startDate, endDate) => {
  if (!startDate || !endDate) return 0;
  
  try {
    const response = await apiCall('/actual-hours-range', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        employee_id: employeeId,
        start_date: startDate,
        end_date: endDate
      })
    });
    
    if (response.success && response.data) {
      // Verwende die gleiche Mitternachts-Korrektur wie bei Sep Ist
      return validateAndCorrectHours(response.data.total_hours, `Custom range ${startDate} to ${endDate} for employee ${employeeId}`);
    }
    return 0;
  } catch (error) {
    console.error('Error calculating custom range hours:', error);
    return 0;
  }
};

// Zusätzliche Sicherheitsfunktion für aggregierte Backend-Summen
const safeSumBackendHours = (logs, fieldName) => {
  const sum = logs.reduce((total, log) => {
    const value = validateAndCorrectHours(log[fieldName] || 0, `${fieldName} for user ${log.firstname} ${log.lastname}`);
    return total + value;
  }, 0);
  
  // Log aggregation info for debugging
  const problemLogs = logs.filter(log => (parseFloat(log[fieldName]) || 0) < 0);
  if (problemLogs.length > 0) {
    const originalSum = logs.reduce((total, log) => total + (parseFloat(log[fieldName]) || 0), 0);
    console.info(`🔍 EmployeeWorkReports: Corrected aggregation for ${fieldName}:`, {
      originalBackendSum: originalSum.toFixed(2),
      correctedFrontendSum: sum.toFixed(2),
      correctionDifference: (sum - originalSum).toFixed(2),
      problemEntries: problemLogs.length,
      problemLogs: problemLogs.map(log => ({ 
        name: `${log.firstname} ${log.lastname}`, 
        originalValue: log[fieldName],
        correctedValue: validateAndCorrectHours(log[fieldName], '')
      }))
    });
  }
  
  return sum;
};

// CSS für 7-Spalten-Wochenlayout
const weekViewStyle = `
  .week-view {
    display: flex;
    flex-wrap: nowrap;
    gap: 4px;
    overflow-x: auto;
  }
  
  .week-day {
    flex: 1 0 calc(14.28% - 3px);
    min-width: 110px;
    max-width: calc(14.28% - 3px);
  }
  
  /* CSS für Overlay von geplanten und tatsächlichen Stunden */
  .shift-overlay-container {
    position: relative;
    margin-bottom: 8px;
  }
  
  .planned-shift-display {
    background-color: #000;
    color: white;
    padding: 4px 6px;
    border-radius: 3px;
    font-size: 0.75rem;
    line-height: 1.2;
    margin-bottom: 2px;
  }
  
  .actual-hours-overlay {
    background-color: #1b5e20;
    color: white;
    padding: 2px 6px;
    border-radius: 3px;
    font-size: 0.7rem;
    line-height: 1.1;
    margin-top: 2px;
    border-left: 3px solid #4caf50;
  }
  
  .hours-comparison {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 0.65rem;
  }
  
  .hours-difference {
    padding: 1px 4px;
    border-radius: 2px;
    font-weight: bold;
  }
  
  .hours-difference.positive {
    background-color: #e8f5e8;
    color: #2e7d32;
  }
  
  .hours-difference.negative {
    background-color: #ffebee;
    color: #c62828;
  }
  
  .hours-difference.neutral {
    background-color: #f5f5f5;
    color: #666;
  }
  
  @media (max-width: 1400px) {
    .week-day {
      min-width: 100px;
    }
  }
  
  @media (max-width: 1200px) {
    .week-day {
      min-width: 95px;
    }
  }
  
  @media (max-width: 900px) {
    .week-day {
      min-width: 90px;
    }
  }
  
  @media (max-width: 600px) {
    .week-day {
      min-width: 85px;
    }
  }
`;

// Zusätzliche Styles für Gesamtstunden-Karten und Tab-Navigation
const totalHoursCardStyles = `
  .bg-light-primary {
    background: linear-gradient(135deg, #e8f5e8 0%, #f0fff0 100%);
    border-left: 4px solid #28a745 !important;
  }
  .bg-light-info {
    background: linear-gradient(135deg, #e6f3ff 0%, #f0f8ff 100%);
    border-left: 4px solid #17a2b8 !important;
  }
  
  /* Verbesserte Tab-Navigation Styles */
  .nav-tabs .nav-link {
    transition: all 0.3s ease;
    margin-right: 2px;
    border-radius: 0.375rem 0.375rem 0 0 !important;
  }
  
  .nav-tabs .nav-link:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
  }
  
  .nav-tabs .nav-link.active {
    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
    transform: translateY(-1px);
  }
`;

// CSS Style hinzufügen
if (!document.getElementById('week-view-styles')) {
  const style = document.createElement('style');
  style.id = 'week-view-styles';
  style.textContent = weekViewStyle + totalHoursCardStyles;
  document.head.appendChild(style);
}

// CSS für die Ampel-Lichter
const logsStyles = {
  confirmationLight: {
    width: '30px',
    height: '30px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto',
    color: 'white',
    fontSize: '14px',
    flexShrink: 0, // Verhindert Schrumpfen
    minWidth: '30px' // Mindestbreite
  },
  confirmed: {
    backgroundColor: '#28a745',
    boxShadow: '0 2px 4px rgba(40, 167, 69, 0.3)'
  },
  pending: {
    backgroundColor: '#ffc107',
    boxShadow: '0 2px 4px rgba(255, 193, 7, 0.3)'
  }
};

const EmployeeWorkReports = ({ user }) => {
  const [selectedEmployee, setSelectedEmployee] = useState(user?.id || null);
  // Einsatzzeiten-Bestaetigung: bisher nur per WhatsApp-Button moeglich
  const [planStatus, setPlanStatus] = useState(null);

  useEffect(() => {
    if (!user?.id || user?.role === 'admin') return;
    apiCall(`/my-shiftplan-status/${user.id}`).then(setPlanStatus).catch(() => setPlanStatus(null));
  }, [user]);

  const replyPlan = async (problem) => {
    try {
      await apiCall(`/my-shiftplan-status/${user.id}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ problem }),
      });
      setMessage(problem ? 'Danke – die Leitung meldet sich bei dir.' : '✅ Danke, deine Einsatzzeiten sind bestätigt!');
      apiCall(`/my-shiftplan-status/${user.id}`).then(setPlanStatus).catch(() => {});
    } catch (_) { setMessage('Hat nicht geklappt – bitte nochmal versuchen.'); }
  };

  const [selectedEmployees, setSelectedEmployees] = useState([]); // Multi-Select für Admins
  const [multiSelectMode, setMultiSelectMode] = useState(false); // Multi-Select Modus
  const [employees, setEmployees] = useState([]);
  // Suchfeld über der Mitarbeiter-Auswahl (Philipp, 21.09.2026: nicht nur Dropdown). Jedes getippte Wort muss im
  // Namen vorkommen – „kr sa" findet Sandra Krstic; Groß/klein und Akzente egal.
  const [empSearch, setEmpSearch] = useState('');
  const normName = (t) => String(t || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
  const empMatches = (emp) => { const hay = normName(`${emp.firstname} ${emp.lastname}`); return normName(empSearch).split(/\s+/).filter(Boolean).every((w) => hay.includes(w)); };
  const empHits = empSearch.trim() ? employees.filter(empMatches) : [];
  const [activeTab, setActiveTab] = useState('combined');
  const [plannedShifts, setPlannedShifts] = useState([]);
  const [actualHours, setActualHours] = useState([]);
  const [employeeData, setEmployeeData] = useState({}); // Daten für mehrere Mitarbeiter
  const [employeeLogs, setEmployeeLogs] = useState([]); // Admin-Logs
  const [confirmationStatus, setConfirmationStatus] = useState({
    planned_confirmed: 0,
    actual_confirmed: 0,
    planned_confirmed_at: null,
    actual_confirmed_at: null
  }); // Mitarbeiter-Bestätigungsstatus
  const [adminActiveTab, setAdminActiveTab] = useState('reports'); // 'reports' oder 'logs'
  const [loading, setLoading] = useState(false);
  // „Dienstplan noch in Arbeit"-Hinweis (gleicher Schalter wie in der Übersicht)
  const [planningFinal, setPlanningFinal] = useState(true);

  useEffect(() => {
    if (user?.role !== 'admin') {
      apiCall('/planning-status')
        .then((d) => setPlanningFinal(!!d.final))
        .catch(() => {});
    }
  }, [user]);
  const [stats, setStats] = useState({});
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear()); // Jahr für Bestätigungen
  const [message, setMessage] = useState(''); // Erfolgsmeldungen
  
  // Filter-States (bleiben beim Wiederaufrufen gespeichert)
  const [hideZeroPlannedHours, setHideZeroPlannedHours] = useState(() => {
    return localStorage.getItem('hideZeroPlannedHours') === 'true';
  });
  const [hideZeroActualHours, setHideZeroActualHours] = useState(() => {
    return localStorage.getItem('hideZeroActualHours') === 'true';
  });

  // Custom Date Range States
  // Standard für die Wiesn 2026 (Philipp, 24.09.): Bereich 1 = 19.–23.09., Bereich 2 = 24.09.–04.10. Einmalig gesetzt
  // (Versionsschlüssel), danach gilt wieder, was man selbst einstellt.
  const RANGE_DEFAULTS = { customRange1Start: '2026-09-19', customRange1End: '2026-09-23', customRange2Start: '2026-09-24', customRange2End: '2026-10-04' };
  try {
    if (localStorage.getItem('customRangesDefaults') !== '2026a') {
      Object.entries(RANGE_DEFAULTS).forEach(([k, v]) => localStorage.setItem(k, v));
      localStorage.setItem('customRangesDefaults', '2026a');
    }
  } catch (_) { /* ohne Speicher: Standardwerte unten */ }
  const [customRange1Start, setCustomRange1Start] = useState(() => {
    return localStorage.getItem('customRange1Start') || RANGE_DEFAULTS.customRange1Start;
  });
  const [customRange1End, setCustomRange1End] = useState(() => {
    return localStorage.getItem('customRange1End') || RANGE_DEFAULTS.customRange1End;
  });
  const [customRange2Start, setCustomRange2Start] = useState(() => {
    return localStorage.getItem('customRange2Start') || RANGE_DEFAULTS.customRange2Start;
  });
  const [customRange2End, setCustomRange2End] = useState(() => {
    return localStorage.getItem('customRange2End') || RANGE_DEFAULTS.customRange2End;
  });
  
  // State für berechnete Custom Range Stunden
  const [customRangeHours, setCustomRangeHours] = useState({});

  const { t } = useTranslation();

  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    if (isAdmin) {
      fetchAllEmployees();
    } else {
      // Für normale Mitarbeiter: automatisch eigene ID setzen
      setSelectedEmployee(user?.id);
    }
    if (multiSelectMode && selectedEmployees.length > 0) {
      fetchMultipleEmployeeData();
    } else if (!multiSelectMode && selectedEmployee) {
      fetchEmployeeData();
      fetchEmployeeConfirmationStatus(selectedEmployee); // Bestätigungsstatus laden
    }
  }, [selectedEmployee, selectedEmployees, activeTab, multiSelectMode, user, selectedYear]);

  useEffect(() => {
    // Lade Admin-Logs wenn der Logs-Tab aktiv ist
    if (isAdmin && adminActiveTab === 'logs') {
      fetchEmployeeLogs();
    }
  }, [adminActiveTab, isAdmin]);

  useEffect(() => {
    // Berechne Custom Range Stunden wenn sich die Datumswerte ändern
    if (employeeLogs.length > 0) {
      calculateCustomRangeHours();
    }
  }, [customRange1Start, customRange1End, customRange2Start, customRange2End, employeeLogs]);

  useEffect(() => {
    // Automatisch den ersten Mitarbeiter auswählen, wenn Admin und noch kein Mitarbeiter ausgewählt
    if (isAdmin && employees.length > 0 && !selectedEmployee && !multiSelectMode) {
      setSelectedEmployee(employees[0].id);
    }
  }, [employees, selectedEmployee, isAdmin, multiSelectMode]);

  // Event Handler für Datumsänderungen
  const handleCustomRange1StartChange = (e) => {
    const value = e.target.value;
    setCustomRange1Start(value);
    localStorage.setItem('customRange1Start', value);
  };

  const handleCustomRange1EndChange = (e) => {
    const value = e.target.value;
    setCustomRange1End(value);
    localStorage.setItem('customRange1End', value);
  };

  const handleCustomRange2StartChange = (e) => {
    const value = e.target.value;
    setCustomRange2Start(value);
    localStorage.setItem('customRange2Start', value);
  };

  const handleCustomRange2EndChange = (e) => {
    const value = e.target.value;
    setCustomRange2End(value);
    localStorage.setItem('customRange2End', value);
  };

  // Berechne Custom Range Stunden für alle Mitarbeiter
  const calculateCustomRangeHours = async () => {
    if (!employeeLogs.length) return;
    
    const newCustomRangeHours = {};
    
    for (const log of employeeLogs) {
      const employeeId = log.id;
      newCustomRangeHours[employeeId] = {};
      
      // Bereich 1
      if (customRange1Start && customRange1End) {
        try {
          const response = await apiCall('/actual-hours-range', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              employee_id: employeeId,
              start_date: customRange1Start,
              end_date: customRange1End
            })
          });
          
          if (response.success && response.data) {
            newCustomRangeHours[employeeId].range1 = validateAndCorrectHours(
              response.data.total_hours, 
              `Custom range 1 ${customRange1Start} to ${customRange1End} for employee ${employeeId}`
            );
          } else {
            newCustomRangeHours[employeeId].range1 = 0;
          }
        } catch (error) {
          console.error(`Error calculating custom range 1 for employee ${employeeId}:`, error);
          newCustomRangeHours[employeeId].range1 = 0;
        }
      } else {
        newCustomRangeHours[employeeId].range1 = 0;
      }
      
      // Bereich 2
      if (customRange2Start && customRange2End) {
        try {
          const response = await apiCall('/actual-hours-range', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              employee_id: employeeId,
              start_date: customRange2Start,
              end_date: customRange2End
            })
          });
          
          if (response.success && response.data) {
            newCustomRangeHours[employeeId].range2 = validateAndCorrectHours(
              response.data.total_hours, 
              `Custom range 2 ${customRange2Start} to ${customRange2End} for employee ${employeeId}`
            );
          } else {
            newCustomRangeHours[employeeId].range2 = 0;
          }
        } catch (error) {
          console.error(`Error calculating custom range 2 for employee ${employeeId}:`, error);
          newCustomRangeHours[employeeId].range2 = 0;
        }
      } else {
        newCustomRangeHours[employeeId].range2 = 0;
      }
    }
    
    setCustomRangeHours(newCustomRangeHours);
  };

  const fetchAllEmployees = async () => {
    try {
      const data = await apiCall('/users');
      console.log('Alle Benutzer geladen:', data);
      setEmployees(data.filter(emp => ['employee', 'lead', 'planner'].includes(emp.role)));
    } catch (error) {
      console.error('Fehler beim Laden der Mitarbeiter:', error);
    }
  };

  const fetchEmployeeData = async () => {
    if (!selectedEmployee) return;
    
    console.log(`Lade Daten für Mitarbeiter ${selectedEmployee}, Jahr ${selectedYear}`);
    setLoading(true);
    try {
      // Lade alle Daten für das ausgewählte Jahr
      const [planned, actual] = await Promise.all([
        apiCall(`/employee/planned-shifts/${selectedEmployee}/${selectedYear}`),
        apiCall(`/employee/actual-hours/${selectedEmployee}/${selectedYear}`)
      ]);

      console.log('Geplante Schichten:', planned);
      console.log('Tatsächliche Stunden:', actual);

      setPlannedShifts(planned);
      calculateStats(planned, 'planned');

      setActualHours(actual);
      if (activeTab === 'actual') {
        calculateStats(actual, 'actual');
      }
    } catch (error) {
      console.error('Fehler beim Laden der Mitarbeiterdaten:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMultipleEmployeeData = async () => {
    if (!selectedEmployees.length) return;
    
    console.log(`Lade Daten für ${selectedEmployees.length} Mitarbeiter, Jahr ${selectedYear}`);
    setLoading(true);
    try {
      const employeePromises = selectedEmployees.map(async (empId) => {
        const [planned, actual] = await Promise.all([
          apiCall(`/employee/planned-shifts/${empId}/${selectedYear}`),
          apiCall(`/employee/actual-hours/${empId}/${selectedYear}`)
        ]);
        
        const employee = employees.find(emp => emp.id === empId);
        return {
          id: empId,
          name: employee?.name || t('reports.unknown'),
          planned,
          actual
        };
      });

      const results = await Promise.all(employeePromises);
      const newEmployeeData = {};
      
      results.forEach(result => {
        newEmployeeData[result.id] = result;
      });
      
      setEmployeeData(newEmployeeData);
      console.log('Multi-Mitarbeiter Daten geladen:', newEmployeeData);
    } catch (error) {
      console.error('Fehler beim Laden der Multi-Mitarbeiterdaten:', error);
    } finally {
      setLoading(false);
    }
  };

  // „Bitte noch nicht abrechnen": Zeile bleibt sichtbar, wird durchgestrichen
  const toggleBillingHold = async (log) => {
    let note = null;
    if (!log.billing_hold) {
      note = window.prompt(`${log.firstname} ${log.lastname}: noch NICHT abrechnen.\nWas musst du noch prüfen? (optional)`, '');
      if (note === null) return; // abgebrochen
    }
    try {
      await apiCall('/admin/billing-hold', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ user_id: log.id, year: new Date().getFullYear(), hold: !log.billing_hold, note }) });
      setEmployeeLogs((list) => list.map((l) => (l.id === log.id ? { ...l, billing_hold: !log.billing_hold, billing_hold_note: note || null } : l)));
    } catch (e) { setMessage('❌ Konnte nicht speichern: ' + e.message); }
  };

  const fetchEmployeeLogs = async () => {
    if (!isAdmin) return;
    
    setLoading(true);
    try {
      const response = await apiCall('/admin/employee-logs-working');
      
      console.log('Rohe Admin Logs vom Backend:', response);
      
      // Das Backend gibt ein Objekt mit employees Array zurück
      const logs = response.employees || response;
      
      // Prüfe, ob logs ein Array ist
      if (!Array.isArray(logs)) {
        console.error('Admin Logs sind kein Array:', typeof logs, logs);
        setEmployeeLogs([]);
        return;
      }
      
      // Das Backend filtert bereits - nur Stunden-Berechnungen hinzufügen
      const enhancedLogs = logs.map(log => ({
        ...log,
        total_planned_hours: log.total_planned_hours || 0,
        total_actual_hours: log.total_actual_hours || 0
      }));
      
      setEmployeeLogs(enhancedLogs);
      console.log('Admin Logs geladen:', enhancedLogs);
    } catch (error) {
      console.error('Fehler beim Laden der Admin-Logs:', error);
      setEmployeeLogs([]);
    } finally {
      setLoading(false);
    }
  };

  const confirmEmployeeHours = async (employeeId, type) => {
    if (!isAdmin) return;
    
    try {
      const response = await apiCall('/admin/confirm-hours', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: employeeId,
          year: selectedYear,
          type: type, // 'planned' oder 'actual'
          admin_id: user?.id || 1 // Verwende echte Admin-User-ID
        }),
      });

      if (response.success) {
        setMessage(`✅ ${response.message}`);
        fetchEmployeeLogs(); // Logs neu laden
        fetchEmployeeConfirmationStatus(employeeId); // Status neu laden
      } else {
        setMessage(`❌ Fehler: ${response.error}`);
      }
    } catch (error) {
      console.error(`Fehler beim Bestätigen der ${type} Stunden:`, error);
      setMessage(`❌ Fehler beim Bestätigen der Stunden: ${error.message}`);
    }
  };

  const fetchEmployeeConfirmationStatus = async (employeeId) => {
    if (!employeeId) return;
    
    try {
      console.log(`Loading confirmation status for employee ${employeeId}, year ${selectedYear}`);
      const status = await apiCall(`/employee/confirmation-status/${employeeId}?year=${selectedYear}`);
      setConfirmationStatus(status || {
        planned_confirmed: 0,
        actual_confirmed: 0,
        planned_confirmed_at: null,
        actual_confirmed_at: null
      });
      console.log('Bestätigungsstatus geladen:', status);
    } catch (error) {
      console.error('Fehler beim Laden des Bestätigungsstatus:', error);
      setConfirmationStatus({
        planned_confirmed: 0,
        actual_confirmed: 0,
        planned_confirmed_at: null,
        actual_confirmed_at: null
      });
    }
  };

  // Filter-Funktionen
  const handlePlannedHoursFilterChange = (checked) => {
    setHideZeroPlannedHours(checked);
    localStorage.setItem('hideZeroPlannedHours', checked.toString());
  };

  const handleActualHoursFilterChange = (checked) => {
    setHideZeroActualHours(checked);
    localStorage.setItem('hideZeroActualHours', checked.toString());
  };

  // Rückgängig-Funktionen
  const unconfirmEmployeeHours = async (employeeId, type) => {
    if (!isAdmin) return;
    
    try {
      const response = await apiCall('/admin/unconfirm-hours', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: employeeId,
          year: selectedYear,
          type: type, // 'planned' oder 'actual'
          admin_id: user?.id || 1 // Verwende echte Admin-User-ID
        }),
      });

      if (response.success) {
        setMessage(`✅ ${response.message}`);
        fetchEmployeeLogs(); // Logs neu laden
        fetchEmployeeConfirmationStatus(employeeId); // Status neu laden
      } else {
        setMessage(`❌ Fehler: ${response.error}`);
      }
    } catch (error) {
      console.error(`Fehler beim Rückgängigmachen der ${type} Stunden:`, error);
      setMessage(`❌ Fehler beim Rückgängigmachen: ${error.message}`);
    }
  };

  const unconfirmOwnHours = async (type) => {
    if (!selectedEmployee) return;
    
    try {
      const response = await apiCall('/employee/unconfirm-hours', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: selectedEmployee,
          year: selectedYear,
          type: type // 'planned' oder 'actual'
        }),
      });

      if (response.success) {
        setMessage(`✅ ${response.message}`);
        
        // WICHTIG: Kurz warten, damit die Datenbank die Änderung verarbeitet
        setTimeout(async () => {
          await fetchEmployeeConfirmationStatus(selectedEmployee); // Status neu laden
        }, 500); // 500ms Delay
        
        // Wenn der Admin die Logs-Ansicht geöffnet hat, diese auch aktualisieren
        if (user?.role === 'admin' && adminActiveTab === 'logs') {
          setTimeout(() => {
            fetchEmployeeLogs();
            console.log('Admin logs refreshed after employee unconfirmation');
          }, 500);
        }
      } else {
        setMessage(`❌ Fehler: ${response.error}`);
      }
    } catch (error) {
      console.error(`Fehler beim Rückgängigmachen der eigenen ${type} Stunden:`, error);
      setMessage(`❌ Fehler beim Rückgängigmachen: ${error.message}`);
    }
  };

  // Hilfsfunktion: Prüft, ob tatsächliche Stunden vorhanden sind
  const hasActualHours = () => {
    if (!actualHours || actualHours.length === 0) return false;
    
    // Prüfe, ob mindestens ein Eintrag tatsächliche Arbeitszeiten hat
    return actualHours.some(entry => {
      const actualStart = entry.start_time;
      const actualEnd = entry.end_time;
      
      // Wenn Start- und Endzeit vorhanden sind und eine gültige Dauer ergeben
      if (actualStart && actualEnd) {
        const hours = calculateHours(actualStart, actualEnd);
        return hours && hours > 0;
      }
      return false;
    });
  };

  const confirmOwnHours = async (type) => {
    if (!selectedEmployee) return;
    
    // Validierung für tatsächliche Stunden
    if (type === 'actual' && !hasActualHours()) {
      setMessage('❌ ' + t('reports.cannotConfirm'));
      return;
    }
    
    console.log(`Confirming own ${type} hours for employee ${selectedEmployee}, year ${selectedYear}`);
    
    try {
      const response = await apiCall('/employee/confirm-hours', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: selectedEmployee,
          year: selectedYear,
          type: type // 'planned' oder 'actual'
        }),
      });

      console.log('Confirmation result:', response);
      
      if (response.success) {
        setMessage(`✅ ${response.message}`);
        
        // WICHTIG: Kurz warten, damit die Datenbank die Änderung verarbeitet
        setTimeout(async () => {
          await fetchEmployeeConfirmationStatus(selectedEmployee); // Status neu laden
          console.log('Confirmation status refreshed');
        }, 500); // 500ms Delay
        
        // Wenn der Admin die Logs-Ansicht geöffnet hat, diese auch aktualisieren
        if (user?.role === 'admin' && adminActiveTab === 'logs') {
          setTimeout(() => {
            fetchEmployeeLogs();
            console.log('Admin logs refreshed after employee confirmation');
          }, 500);
        }
      } else {
        setMessage(`❌ Fehler: ${response.error}`);
      }
    } catch (error) {
      console.error(`Fehler beim Bestätigen der eigenen ${type} Stunden:`, error);
      setMessage(`❌ Fehler beim Bestätigen der Stunden: ${error.message}`);
    }
  };

  // ICS-Export für Kalender (iPhone & Android) - NUR für geplante Schichten
  const exportToCalendar = () => {
    console.log('Export to Calendar called');
    console.log('activeTab:', activeTab);
    console.log('plannedShifts:', plannedShifts);
    console.log('plannedShifts length:', plannedShifts?.length);
    
    // Nur geplante Schichten exportieren, niemals tatsächliche Stunden
    if (activeTab !== 'planned') {
      setMessage('❌ ' + t('reports.calendarError'));
      return;
    }
    
    if (!plannedShifts || plannedShifts.length === 0) {
      setMessage('❌ ' + t('reports.noShiftsExport'));
      console.log('No planned shifts available for export');
      return;
    }

    const employeeName = selectedEmployeeName();
    console.log('Employee name:', employeeName);
    
    // ICS-Header
    let icsContent = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//Personalplanung//Geplante-Arbeitszeiten//DE',
      'CALSCALE:GREGORIAN',
      'METHOD:PUBLISH',
      `X-WR-CALNAME:Geplante Schichten ${employeeName} ${selectedYear}`,
      'X-WR-TIMEZONE:Europe/Berlin'
    ];

    // Jede GEPLANTE Schicht als Event hinzufügen
    plannedShifts.forEach((shift, index) => {
      console.log(`Processing planned shift ${index}:`, shift);
      
      const startDate = new Date(`${shift.date}T${shift.start_time}`);
      const endDate = new Date(`${shift.date}T${shift.end_time}`);
      
      console.log(`Start date: ${startDate}, End date: ${endDate}`);
      
      // Formatiere Datum für ICS (YYYYMMDDTHHMMSS)
      const formatICSDate = (date) => {
        return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
      };
      
      // Unique ID für das Event
      const uid = `shift-${selectedEmployee}-${shift.date}-${shift.start_time}-${index}@personalplanung.local`;
      
      // Station-Name aus station_id ableiten falls leer
      const getStationName = (shift) => {
        if (shift.station_name && shift.station_name !== '') {
          return shift.station_name;
        }
        
        // Fallback: Station-Name aus station_id ableiten
        const stationId = shift.station_id;
        if (stationId === 'G1') return 'Station G1';
        if (stationId === 'G5') return 'Station G5';
        if (stationId === 'G8') return 'Station G8';
        if (stationId === 'g10') return 'Station G10';
        if (stationId === '1') return t('reports.kitchen');
        if (stationId === '2') return t('reports.service');
        if (stationId === '3') return t('reports.bar');
        if (stationId === '4') return t('reports.cashRegister');
        return stationId || t('reports.unknownStation');
      };
      
      const stationName = getStationName(shift);
      
      // Beschreibung mit Details für geplante Schicht
      const description = [
        `GEPLANTE Arbeitsschicht: ${shift.start_time} - ${shift.end_time}`,
        `Station: ${stationName}`,
        `Geplante Dauer: ${calculateHours(shift.start_time, shift.end_time)} Stunden`,
        `Mitarbeiter: ${employeeName}`,
        ``,
        `💡 Dies ist eine geplante Schicht aus der Personalplanung.`
      ].join('\\n');

      icsContent.push(
        'BEGIN:VEVENT',
        `UID:${uid}`,
        `DTSTAMP:${formatICSDate(new Date())}`,
        `DTSTART:${formatICSDate(startDate)}`,
        `DTEND:${formatICSDate(endDate)}`,
        `SUMMARY:🗓️ Geplante Schicht - ${stationName}`,
        `DESCRIPTION:${description}`,
        `LOCATION:${stationName}`,
        'STATUS:CONFIRMED',
        'TRANSP:OPAQUE',
        'END:VEVENT'
      );
    });

    icsContent.push('END:VCALENDAR');
    
    console.log('Generated ICS content:', icsContent.join('\r\n'));
    console.log(`Total events created: ${plannedShifts.length}`);

    // ICS-Datei zum Download anbieten
    const icsBlob = new Blob([icsContent.join('\r\n')], { 
      type: 'text/calendar;charset=utf-8' 
    });
    
    const url = URL.createObjectURL(icsBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `arbeitszeiten-${employeeName.replace(/\s+/g, '-')}-${selectedYear}.ics`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    setMessage('✅ ' + t('reports.exported'));
    console.log('Planned shifts calendar export completed successfully');
  };

  const calculateStats = (data, type) => {
    if (!data.length) {
      setStats({});
      return;
    }

    const totalEntries = data.length;
    let totalHours = 0;
    const stationCounts = {};
    const monthCounts = {};

    data.forEach(entry => {
      // Stunden berechnen
      let hours = 0;
      if (type === 'planned') {
        hours = calculateHours(entry.start_time, entry.end_time);
      } else {
        hours = calculateHours(entry.start_time, entry.end_time);
      }
      totalHours += parseFloat(hours);

      // Stationen zählen
      const stationName = getStationName(entry.station_id);
      stationCounts[stationName] = (stationCounts[stationName] || 0) + 1;

      // Monate zählen
      const month = new Date(entry.date).toLocaleDateString('de-DE', { month: 'long' });
      monthCounts[month] = (monthCounts[month] || 0) + 1;
    });

    setStats({
      totalEntries,
      totalHours: totalHours.toFixed(2),
      avgHoursPerShift: (totalHours / totalEntries).toFixed(2),
      stationCounts,
      monthCounts,
      topStation: Object.keys(stationCounts).reduce((a, b) => 
        stationCounts[a] > stationCounts[b] ? a : b, Object.keys(stationCounts)[0])
    });
  };

  const calculateHours = (start, end) => {
    if (!start || !end) return 0;
    
    const [startHour, startMin] = start.split(':').map(Number);
    const [endHour, endMin] = end.split(':').map(Number);
    
    const startMinutes = startHour * 60 + startMin;
    let endMinutes = endHour * 60 + endMin;
    
    if (endMinutes < startMinutes) {
      endMinutes += 24 * 60;
    }
    
    const diffMinutes = endMinutes - startMinutes;
    return diffMinutes / 60; // Return number, not string
  };

  const groupByMonth = (data) => {
    return data.reduce((acc, entry) => {
      const date = new Date(entry.date);
      const monthKey = date.toLocaleDateString('de-DE', { year: 'numeric', month: 'long' });
      if (!acc[monthKey]) {
        acc[monthKey] = [];
      }
      acc[monthKey].push(entry);
      return acc;
    }, {});
  };

  // Neue Funktion: Gruppiert zuerst nach Monat, dann nach Tag
  const groupByMonthAndDay = (data) => {
    return data.reduce((acc, entry) => {
      const date = new Date(entry.date);
      const monthKey = date.toLocaleDateString('de-DE', { year: 'numeric', month: 'long' });
      const dayKey = entry.date; // YYYY-MM-DD Format

      if (!acc[monthKey]) {
        acc[monthKey] = {};
      }
      if (!acc[monthKey][dayKey]) {
        acc[monthKey][dayKey] = [];
      }
      acc[monthKey][dayKey].push(entry);
      return acc;
    }, {});
  };

  // Neue Funktion: Gruppiert nach Wochen (Montag-Sonntag)
  const groupByWeeks = (data) => {
    const weeks = {};
    
    data.forEach(entry => {
      const date = new Date(entry.date);
      
      // Finde den Montag dieser Woche
      const dayOfWeek = date.getDay(); // 0=Sonntag, 1=Montag, ..., 6=Samstag
      const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Korrigiere für Sonntag
      const monday = new Date(date);
      monday.setDate(date.getDate() - daysToMonday);
      
      // Erstelle Wochen-Key (YYYY-MM-DD des Montags)
      const weekKey = monday.toISOString().split('T')[0];
      
      if (!weeks[weekKey]) {
        weeks[weekKey] = {
          monday: monday,
          days: {}
        };
      }
      
      const dayKey = entry.date;
      if (!weeks[weekKey].days[dayKey]) {
        weeks[weekKey].days[dayKey] = [];
      }
      weeks[weekKey].days[dayKey].push(entry);
    });
    
    return weeks;
  };

  // Hilfsfunktion: Erstelle alle 7 Tage einer Woche
  const getWeekDays = (mondayDate) => {
    const days = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(mondayDate);
      day.setDate(mondayDate.getDate() + i);
      days.push({
        date: day.toISOString().split('T')[0],
        dayName: day.toLocaleDateString('de-DE', { weekday: 'short' }),
        dayNumber: day.getDate(),
        isToday: day.toDateString() === new Date().toDateString()
      });
    }
    return days;
  };

  // Hilfsfunktion: Finde tatsächliche Stunden für eine geplante Schicht
  const getActualHoursForPlannedShift = (plannedShift, actualHoursData) => {
    if (!actualHoursData || !Array.isArray(actualHoursData)) return null;
    
    // Mit Pause geteilte Einträge (gleicher Tag + Station) zu EINEM Block zusammenführen: Beginn des ersten, Ende des
    // letzten Teils, Stunden = Summe der Teile, Pausen dazwischen (werden nicht bezahlt).
    const parts = actualHoursData.filter(actual => actual.date === plannedShift.date && actual.station_id === plannedShift.station_id);
    if (parts.length <= 1) return parts[0];
    const m = (t) => { const [h, mm] = String(t).split(':').map(Number); return (h < 6 ? h + 24 : h) * 60 + mm; };
    const segs = [...parts].sort((a, b) => m(a.start_time) - m(b.start_time));
    const hours = segs.reduce((s, x) => s + calculateHours(x.start_time, x.end_time), 0);
    const tm = (t) => { const [h, mm] = String(t).split(':').map(Number); return h * 60 + mm; };
    const pauseMin = segs.slice(1).reduce((s, x, i) => { let d = tm(x.start_time) - tm(segs[i].end_time); if (d < 0) d += 1440; return s + d; }, 0);
    const pauses = [`${pauseMin} Min`];
    return { ...segs[0], end_time: segs[segs.length - 1].end_time, hours, pauses, segments: segs };
  };

  // Hilfsfunktion: Berechne Stunden aus tatsächlichen Start/End Zeiten
  const calculateActualHours = (actualStartTime, actualEndTime) => {
    if (!actualStartTime || !actualEndTime) return 0;
    
    // Verwende die gleiche Logik wie calculateHours
    return calculateHours(actualStartTime, actualEndTime);
  };

  // Hilfsfunktion für chronologische Monats-Sortierung
  const sortMonthsChronologically = (monthEntries) => {
    return monthEntries.sort(([monthA], [monthB]) => {
      // Extrahiere Jahr und Monat aus den String-Keys (z.B. "2025 September")
      const dateA = new Date(monthA.split(' ')[1] + ' 1, ' + monthA.split(' ')[0]);
      const dateB = new Date(monthB.split(' ')[1] + ' 1, ' + monthB.split(' ')[0]);
      return dateA - dateB; // Aufsteigend sortieren
    });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', { 
      weekday: 'short', 
      day: '2-digit', 
      month: '2-digit' 
    });
  };

  const selectedEmployeeName = () => {
    if (selectedEmployee === user?.id) {
      return `${user.firstname} ${user.lastname} (Sie)`;
    }
    const emp = employees.find(e => e.id === selectedEmployee);
    return emp ? `${emp.firstname} ${emp.lastname}` : t('reports.unknown');
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="sr-only">{t('reports.loading')}</span>
        </div>
        <p className="mt-2">{t('reports.loadingYear', { year: selectedYear })}</p>
      </div>
    );
  }

  return (
    <div className="container-fluid py-4">
      {/* Inline CSS für Gesamtstunden-Karten */}
      <style>{totalHoursCardStyles}</style>
      
      <div className="row mb-4">
        <div className="col-12">
          <h2 className="mb-4">
            <i className="fas fa-chart-line me-2 text-primary"></i>
            {t('reports.title', { year: selectedYear })}
          </h2>

          {/* Einsatzzeiten bestätigen – hier, wo man sie gerade vor sich hat */}
          {user?.role !== 'admin' && planStatus && planStatus.needsConfirm && (
            <div className="alert alert-warning border-warning shadow-sm d-flex flex-wrap align-items-center justify-content-between gap-2"
              style={{ borderLeftWidth: '6px' }}>
              <div>
                <strong>⏰ Bitte bestätige deine Einsatzzeiten</strong>
                <div className="small">Passt alles so, wie es unten steht?</div>
              </div>
              <div className="d-flex gap-2">
                <button className="btn btn-success" onClick={() => replyPlan(false)}>Passt so – bestätigen</button>
                <button className="btn btn-outline-secondary" onClick={() => replyPlan(true)}>Es gibt ein Problem</button>
              </div>
            </div>
          )}
          {user?.role !== 'admin' && planStatus && planStatus.confirmed && (
            <div className="alert alert-success py-2"><i className="bi bi-check-circle me-1"></i>
              Deine Einsatzzeiten sind bestätigt. Danke!</div>
          )}

          {/* Großer Hinweis, solange der Dienstplan nicht final ist */}
          {user?.role !== 'admin' && !planningFinal && (
            <div className="alert alert-warning border-warning shadow-sm" style={{ borderLeftWidth: '6px' }}>
              <h4 className="alert-heading mb-2">{t('overview.planningDraftTitle')}</h4>
              <p className="mb-0" style={{ fontSize: '1.05rem' }}>{t('overview.planningDraftText')}</p>
            </div>
          )}

          {/* Success/Error Messages */}
          {message && (
            <div className={`alert ${message.includes('✅') ? 'alert-success' : 'alert-danger'} alert-dismissible fade show`} role="alert">
              {message}
              <button type="button" className="btn-close" onClick={() => setMessage('')}></button>
            </div>
          )}

          {/* Year Selection */}
          <div className="card mb-4">
            <div className="card-body">
              <div className="row align-items-center">
                <div className="col-md-3">
                  <label htmlFor="yearSelect" className="form-label">{t('reports.selectYear')}</label>
                  <select
                    id="yearSelect"
                    className="form-select"
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                  >
                    {[new Date().getFullYear() - 2, new Date().getFullYear() - 1, new Date().getFullYear(), new Date().getFullYear() + 1].map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>
                <div className="col-md-9">
                  <small className="text-muted">
                    {t('reports.yearHint')}
                  </small>
                </div>
              </div>
            </div>
          </div>

          {/* Admin Main Navigation */}
          {user?.role === 'admin' && (
            <div className="card mb-4">
              <div className="card-header">
                <ul className="nav nav-tabs card-header-tabs">
                  <li className="nav-item">
                    <button
                      className={`nav-link ${adminActiveTab === 'reports' ? 'active' : ''}`}
                      onClick={() => setAdminActiveTab('reports')}
                      style={{
                        backgroundColor: adminActiveTab === 'reports' ? '#007bff' : '#f8f9fa',
                        color: adminActiveTab === 'reports' ? 'white' : '#495057',
                        border: '1px solid #dee2e6',
                        fontWeight: adminActiveTab === 'reports' ? 'bold' : 'normal'
                      }}
                    >
                      <i className="fas fa-user me-2"></i>
                      Mitarbeiter einzeln
                    </button>
                  </li>
                  <li className="nav-item">
                    <button
                      className={`nav-link ${adminActiveTab === 'logs' ? 'active' : ''}`}
                      onClick={() => setAdminActiveTab('logs')}
                      style={{
                        backgroundColor: adminActiveTab === 'logs' ? '#28a745' : '#f8f9fa',
                        color: adminActiveTab === 'logs' ? 'white' : '#495057',
                        border: '1px solid #dee2e6',
                        fontWeight: adminActiveTab === 'logs' ? 'bold' : 'normal'
                      }}
                    >
                      <i className="fas fa-users me-2"></i>
                      Alle Mitarbeiter
                    </button>
                  </li>
                </ul>
              </div>
            </div>
          )}

          {/* Employee Selection (Admin only) */}
          {user?.role === 'admin' && adminActiveTab === 'reports' && (
            <div className="card mb-4">
              <div className="card-header bg-primary text-white">
                <i className="fas fa-user-circle me-2"></i>
                Mitarbeiter auswählen
                
                {/* Multi-Select Toggle */}
                <div className="form-check form-switch float-end">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="multiSelectToggle"
                    checked={multiSelectMode}
                    onChange={(e) => {
                      setMultiSelectMode(e.target.checked);
                      if (!e.target.checked) {
                        setSelectedEmployees([]);
                        setEmployeeData({});
                      } else {
                        setSelectedEmployee(null);
                      }
                    }}
                  />
                  <label className="form-check-label text-white" htmlFor="multiSelectToggle">
                    Mehrfachauswahl
                  </label>
                </div>
              </div>
              <div className="card-body">
                <div className="input-group mb-2">
                  <span className="input-group-text"><i className="bi bi-search"></i></span>
                  <input
                    type="search"
                    className="form-control"
                    placeholder="Name suchen … (Enter wählt den ersten Treffer)"
                    value={empSearch}
                    onChange={(e) => setEmpSearch(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') setEmpSearch('');
                      if (e.key !== 'Enter' || !empHits.length) return;
                      const first = empHits[0];
                      if (!multiSelectMode) { setSelectedEmployee(first.id); setEmpSearch(''); }
                      else if (!selectedEmployees.includes(first.id) && selectedEmployees.length < 5) { setSelectedEmployees([...selectedEmployees, first.id]); setEmpSearch(''); }
                    }}
                  />
                  {empSearch && <button type="button" className="btn btn-outline-secondary" onClick={() => setEmpSearch('')}>×</button>}
                </div>
                {!multiSelectMode && empSearch.trim() && (
                  <div className="list-group mb-2" style={{ maxHeight: 260, overflowY: 'auto' }}>
                    {empHits.slice(0, 30).map((emp) => (
                      <button key={emp.id} type="button" className={`list-group-item list-group-item-action py-1 ${emp.id === selectedEmployee ? 'active' : ''}`}
                        onClick={() => { setSelectedEmployee(emp.id); setEmpSearch(''); }}>
                        {emp.firstname} {emp.lastname}
                      </button>
                    ))}
                    {!empHits.length && <div className="list-group-item py-1 text-muted">Kein Treffer für „{empSearch}"</div>}
                  </div>
                )}
                {!multiSelectMode ? (
                  // Single Employee Selection
                  <select
                    className="form-select"
                    value={selectedEmployee || ''}
                    onChange={(e) => setSelectedEmployee(parseInt(e.target.value))}
                  >
                    <option value="">Mitarbeiter auswählen...</option>
                    {employees.map(emp => (
                      <option key={emp.id} value={emp.id}>
                        {emp.firstname} {emp.lastname}
                      </option>
                    ))}
                  </select>
                ) : (
                  // Multi Employee Selection
                  <div>
                    <div className="mb-3">
                      <small className="text-muted">
                        Mehrere Mitarbeiter auswählen (max. 5):
                      </small>
                    </div>
                    <div className="row">
                      {employees.filter((emp) => !empSearch.trim() || empMatches(emp) || selectedEmployees.includes(emp.id)).map(emp => (
                        <div key={emp.id} className="col-md-6 col-lg-4 mb-2">
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id={`emp-${emp.id}`}
                              checked={selectedEmployees.includes(emp.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  if (selectedEmployees.length < 5) {
                                    setSelectedEmployees([...selectedEmployees, emp.id]);
                                  }
                                } else {
                                  setSelectedEmployees(selectedEmployees.filter(id => id !== emp.id));
                                }
                              }}
                              disabled={!selectedEmployees.includes(emp.id) && selectedEmployees.length >= 5}
                            />
                            <label className="form-check-label" htmlFor={`emp-${emp.id}`}>
                              {emp.firstname} {emp.lastname}
                            </label>
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    {selectedEmployees.length > 0 && (
                      <div className="mt-3">
                        <small className="text-success">
                          {selectedEmployees.length} Mitarbeiter ausgewählt
                        </small>
                        <button
                          className="btn btn-sm btn-outline-secondary ms-2"
                          onClick={() => setSelectedEmployees([])}
                        >
                          Alle abwählen
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Content Area - Single Employee */}
          {selectedEmployee && !multiSelectMode && (user?.role !== 'admin' || adminActiveTab === 'reports') && (
            <>
              {/* Statistics Cards */}
              {Object.keys(stats).length > 0 && (
                <div className="row mb-4">
                  <div className="col-md-3 mb-3">
                    <div className="card border-0 shadow-sm h-100">
                      <div className="card-body text-center">
                        <i className="fas fa-calendar-check text-success mb-2" style={{fontSize: '2rem'}}></i>
                        <h5 className="card-title">Gesamt-Schichten</h5>
                        <h3 className="text-success">{stats.totalEntries || 0}</h3>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-3 mb-3">
                    <div className="card border-0 shadow-sm h-100">
                      <div className="card-body text-center">
                        <i className="fas fa-clock text-info mb-2" style={{fontSize: '2rem'}}></i>
                        <h5 className="card-title">Gesamt-Stunden</h5>
                        <h3 className="text-info">{stats.totalHours || 0}h</h3>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-3 mb-3">
                    <div className="card border-0 shadow-sm h-100">
                      <div className="card-body text-center">
                        <i className="fas fa-chart-bar text-warning mb-2" style={{fontSize: '2rem'}}></i>
                        <h5 className="card-title">Ø Stunden/Schicht</h5>
                        <h3 className="text-warning">{stats.avgHoursPerShift || 0}h</h3>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-3 mb-3">
                    <div className="card border-0 shadow-sm h-100">
                      <div className="card-body text-center">
                        <i className="fas fa-building text-primary mb-2" style={{fontSize: '2rem'}}></i>
                        <h5 className="card-title">Top Station</h5>
                        <h3 className="text-primary">{stats.topStation || '-'}</h3>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Tab Navigation */}
              <div className="card">
                <div className="card-header">
                  <div className="d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">
                      <i className="fas fa-user me-2"></i>
                      {selectedEmployeeName()}
                    </h5>
                    <div className="d-flex align-items-center">
                      {/* Kalender-Export Button nur bei geplanten Schichten anzeigen */}
                      {activeTab === 'planned' && plannedShifts && plannedShifts.length > 0 && (
                        <button
                          className="btn btn-success btn-sm me-3"
                          onClick={exportToCalendar}
                          title="Alle geplanten Schichten als Kalender-Datei exportieren (für iPhone & Android)"
                          style={{
                            backgroundColor: '#28a745',
                            borderColor: '#28a745',
                            color: 'white',
                            fontWeight: '500'
                          }}
                        >
                          <i className="fas fa-calendar-download me-2"></i>
                          {t('reports.exportCalendar')}
                        </button>
                      )}
                      <ul className="nav nav-tabs card-header-tabs mb-0">
                        <li className="nav-item">
                          <button
                            className={`nav-link ${activeTab === 'combined' ? 'active' : ''}`}
                            onClick={() => setActiveTab('combined')}
                          >
                            <i className="fas fa-layer-group me-2"></i>
                            Übersicht (Geplant + Ist)
                          </button>
                        </li>
                        <li className="nav-item">
                          <button
                            className={`nav-link ${activeTab === 'planned' ? 'active' : ''}`}
                            onClick={() => setActiveTab('planned')}
                          >
                            <i className="fas fa-calendar-alt me-2"></i>
                            {t('reports.tabShifts')}
                          </button>
                        </li>
                        <li className="nav-item">
                          <button
                            className={`nav-link ${activeTab === 'actual' ? 'active' : ''}`}
                            onClick={() => setActiveTab('actual')}
                          >
                            <i className="fas fa-clock-rotate-left me-2"></i>
                            {t('reports.tabTimes')}
                          </button>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="card-body">
                  {activeTab === 'combined' && (
                    <CombinedView 
                      plannedShifts={plannedShifts}
                      actualHours={actualHours}
                      sortMonthsChronologically={sortMonthsChronologically}
                      formatDate={formatDate}
                      calculateHours={calculateHours}
                      getActualHoursForPlannedShift={getActualHoursForPlannedShift}
                      calculateActualHours={calculateActualHours}
                      getStationName={getStationName}
                    />
                  )}

                  {activeTab === 'planned' && (
                    <PlannedShiftsView 
                      shifts={plannedShifts} 
                      sortMonthsChronologically={sortMonthsChronologically}
                      formatDate={formatDate}
                      calculateHours={calculateHours}
                    />
                  )}

                  {activeTab === 'actual' && (
                    <ActualHoursView 
                      hours={actualHours} 
                      sortMonthsChronologically={sortMonthsChronologically}
                      formatDate={formatDate}
                      calculateHours={calculateHours}
                    />
                  )}
                </div>
              </div>

              {/* Employee Hour Confirmation Section */}
              {confirmationStatus && (
                <div className="card mb-4">
                  <div className="card-header bg-info text-white">
                    <h5 className="mb-0">
                      <i className="fas fa-clipboard-check me-2"></i>
                      {t('reports.hourConfirmation')} {selectedYear}
                    </h5>
                  </div>
                  <div className="card-body">
                    {/* Gesamtstunden Übersicht für Mitarbeiter */}
                    <div className="row mb-4">
                      <div className="col-md-6 mb-3">
                        <div className="card border-success h-100" style={{backgroundColor: '#f8f9fa', borderLeft: '4px solid #28a745'}}>
                          <div className="card-body text-center">
                            <i className="fas fa-calendar-check text-success mb-2" style={{fontSize: '2rem'}}></i>
                            <h6 className="card-title text-success">Ihre Gesamtstunden Geplant</h6>
                            <h3 className="text-success mb-0">
                              {stats.totalHours || 0}h
                            </h3>
                            <small className="text-muted">
                              Aus {stats.totalEntries || 0} geplanten Schichten
                            </small>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-6 mb-3">
                        <div className="card border-info h-100" style={{backgroundColor: '#f8f9fa', borderLeft: '4px solid #17a2b8'}}>
                          <div className="card-body text-center">
                            <i className="fas fa-clock text-info mb-2" style={{fontSize: '2rem'}}></i>
                            <h6 className="card-title text-info">Ihre Gesamtstunden Tatsächlich</h6>
                            <h3 className="text-info mb-0">
                              {(() => {
                                const totalActualHours = actualHours.reduce((sum, entry) => 
                                  sum + (calculateHours(entry.start_time, entry.end_time) || 0), 0);
                                return totalActualHours.toFixed(1);
                              })()}h
                            </h3>
                            <small className="text-muted">
                              Aus {actualHours.length || 0} erfassten Einträgen
                            </small>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <div className="confirmation-card h-100">
                          <div className="d-flex align-items-center mb-3">
                            <div 
                              className="me-3"
                              style={{
                                ...logsStyles.confirmationLight,
                                ...(confirmationStatus?.planned_confirmed ? logsStyles.confirmed : logsStyles.pending)
                              }}
                            >
                              <i className={`fas ${confirmationStatus?.planned_confirmed ? 'fa-check' : 'fa-clock'}`}></i>
                            </div>
                            <div>
                              <h6 className="mb-1">Geplante Schichten</h6>
                              <small className="text-muted">
                                {confirmationStatus?.planned_confirmed ? 'Bestätigt' : 'Ausstehend'}
                              </small>
                            </div>
                          </div>
                          
                          {confirmationStatus?.planned_confirmed ? (
                            <div>
                              <div className="alert alert-success mb-2">
                                <i className="fas fa-check-circle me-2"></i>
                                <strong>Bestätigt</strong><br/>
                                <small>
                                  am {new Date(confirmationStatus.planned_confirmed_at).toLocaleDateString('de-DE')}
                                </small>
                              </div>
                              <button
                                className="btn btn-outline-warning btn-sm w-100"
                                onClick={() => unconfirmOwnHours('planned')}
                              >
                                <i className="fas fa-undo me-2"></i>
                                Bestätigung rückgängig machen
                              </button>
                            </div>
                          ) : (
                            <button
                              className="btn btn-primary w-100"
                              onClick={() => confirmOwnHours('planned')}
                            >
                              <i className="fas fa-calendar-check me-2"></i>
                              Geplante Stunden bestätigen
                            </button>
                          )}
                        </div>
                      </div>
                      
                      <div className="col-md-6 mb-3">
                        <div className="confirmation-card h-100">
                          <div className="d-flex align-items-center mb-3">
                            <div 
                              className="me-3"
                              style={{
                                ...logsStyles.confirmationLight,
                                ...(confirmationStatus?.actual_confirmed ? logsStyles.confirmed : logsStyles.pending)
                              }}
                            >
                              <i className={`fas ${confirmationStatus?.actual_confirmed ? 'fa-check' : 'fa-clock'}`}></i>
                            </div>
                            <div>
                              <h6 className="mb-1">Geleistete Stunden</h6>
                              <small className="text-muted">
                                {confirmationStatus?.actual_confirmed ? 'Bestätigt' : 'Ausstehend'}
                              </small>
                            </div>
                          </div>
                          
                          {confirmationStatus?.actual_confirmed ? (
                            <div>
                              <div className="alert alert-success mb-2">
                                <i className="fas fa-check-circle me-2"></i>
                                <strong>Bestätigt</strong><br/>
                                <small>
                                  am {new Date(confirmationStatus.actual_confirmed_at).toLocaleDateString('de-DE')}
                                </small>
                              </div>
                              <button
                                className="btn btn-outline-warning btn-sm w-100"
                                onClick={() => unconfirmOwnHours('actual')}
                              >
                                <i className="fas fa-undo me-2"></i>
                                Bestätigung rückgängig machen
                              </button>
                            </div>
                          ) : (
                            <>
                              {hasActualHours() ? (
                                <button
                                  className="btn btn-primary w-100"
                                  onClick={() => confirmOwnHours('actual')}
                                >
                                  <i className="fas fa-clock me-2"></i>
                                  Geleistete Stunden bestätigen
                                </button>
                              ) : (
                                <div>
                                  <button
                                    className="btn btn-secondary w-100"
                                    disabled
                                    title="Keine geleisteten Stunden erfasst"
                                  >
                                    <i className="fas fa-clock me-2"></i>
                                    Geleistete Stunden bestätigen
                                  </button>
                                  <small className="text-muted mt-2 d-block">
                                    <i className="fas fa-info-circle me-1"></i>
                                    Noch keine tatsächlichen Arbeitszeiten erfasst
                                  </small>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="mt-3">
                      <div className="alert alert-light mb-0">
                        <i className="fas fa-info-circle me-2"></i>
                        <strong>Hinweis:</strong> Bitte bestätigen Sie Ihre Arbeitszeiten für das Jahr {selectedYear}. 
                        Geplante Stunden können sofort bestätigt werden. Tatsächliche Stunden können erst bestätigt werden, 
                        nachdem die geleisteten Arbeitszeiten erfasst wurden.
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Content Area - Multiple Employees */}
          {multiSelectMode && selectedEmployees.length > 0 && adminActiveTab === 'reports' && (
            <div className="card">
              <div className="card-header bg-info text-white">
                <h5 className="mb-0">
                  <i className="fas fa-users me-2"></i>
                  Familienplan: {selectedEmployees.length} Mitarbeiter
                </h5>
              </div>
              <div className="card-body">
                {loading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Lade Daten...</span>
                    </div>
                    <p className="mt-3">Lade Mitarbeiter-Daten...</p>
                  </div>
                ) : (
                  <div>
                    {/* Übersichtskarten */}
                    <div className="row mb-4">
                      {selectedEmployees.map(empId => {
                        const employee = employeeData[empId];
                        if (!employee) return null;
                        
                        const plannedCount = employee.planned?.length || 0;
                        const actualCount = employee.actual?.length || 0;
                        const plannedHours = employee.planned?.reduce((sum, shift) => 
                          sum + (calculateHours(shift.start_time, shift.end_time) || 0), 0) || 0;
                                                        const actualHours = employee.actual?.reduce((sum, shift) => 
                                                          sum + (calculateHours(shift.start_time, shift.end_time) || 0), 0) || 0;                        return (
                          <div key={empId} className="col-md-6 col-lg-4 mb-3">
                            <div className="card h-100 border-0 shadow-sm">
                              <div className="card-header bg-light">
                                <h6 className="mb-0 text-primary">
                                  <i className="fas fa-user me-1"></i>
                                  {employee.name}
                                </h6>
                              </div>
                              <div className="card-body text-center">
                                <div className="row">
                                  <div className="col-6">
                                    <div className="border-end">
                                      <h5 className="text-success mb-1">{plannedCount}</h5>
                                      <small className="text-muted">Schichten</small>
                                      <br/>
                                      <small className="text-success">{(plannedHours || 0).toFixed(1)}h</small>
                                    </div>
                                  </div>
                                  <div className="col-6">
                                    <h5 className="text-warning mb-1">{actualCount}</h5>
                                    <small className="text-muted">Ist-Stunden</small>
                                    <br/>
                                    <small className="text-warning">{(actualHours || 0).toFixed(1)}h</small>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Detaillierte Einsatzpläne */}
                    <div className="mt-4">
                      <h6 className="mb-3">
                        <i className="fas fa-calendar-alt me-2"></i>
                        Detaillierte Einsatzpläne
                      </h6>
                      
                      {selectedEmployees.map(empId => {
                        const employee = employeeData[empId];
                        if (!employee || !employee.planned?.length) return null;

                        // Gruppiere Schichten nach Monat
                        const groupedShifts = groupByWeeks(employee.planned);
                        
                        return (
                          <div key={empId} className="mb-4">
                            <div className="card border-primary">
                              <div className="card-header bg-primary text-white">
                                <h6 className="mb-0">
                                  <i className="fas fa-user-circle me-2"></i>
                                  {employee.name} - Geplante Einsätze
                                </h6>
                              </div>
                              <div className="card-body">
                                {Object.keys(groupedShifts).length === 0 ? (
                                  <div className="text-center text-muted py-3">
                                    <i className="fas fa-calendar-times fa-2x mb-2"></i>
                                    <p>Keine geplanten Schichten für dieses Jahr</p>
                                  </div>
                                ) : (
                                  Object.entries(groupedShifts)
                                    .sort(([weekA], [weekB]) => new Date(weekA) - new Date(weekB))
                                    .map(([weekKey, weekData]) => {
                                      const weekDays = getWeekDays(weekData.monday);
                                      const totalShiftsInWeek = Object.values(weekData.days).flat().length;
                                      const totalHoursInWeek = Object.values(weekData.days).flat()
                                        .reduce((sum, shift) => sum + calculateHours(shift.start_time, shift.end_time), 0);
                                      
                                      // Formatiere Wochenbeschreibung
                                      const startDate = weekData.monday.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
                                      const endDate = new Date(weekData.monday.getTime() + 6 * 24 * 60 * 60 * 1000)
                                        .toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });

                                      return (
                                        <div key={weekKey} className="mb-4">
                                          <h6 className="text-primary border-bottom pb-2 mb-3">
                                            <i className="fas fa-calendar-week me-1"></i>
                                            Woche {startDate} - {endDate}
                                            <span className="badge bg-primary ms-2">{totalShiftsInWeek} Schichten</span>
                                            <span className="badge bg-success ms-1">{totalHoursInWeek.toFixed(1)}h</span>
                                          </h6>
                                          <div className="week-view">
                                            {weekDays.map((dayInfo) => {
                                              const shiftsOnDay = weekData.days[dayInfo.date] || [];
                                              const sortedShifts = shiftsOnDay.sort((a, b) => 
                                                a.start_time.localeCompare(b.start_time)
                                              );
                                              
                                              // Berechne Gesamtstunden für diesen Tag
                                              const totalHoursOnDay = sortedShifts.reduce((sum, shift) => 
                                                sum + calculateHours(shift.start_time, shift.end_time), 0
                                              );

                                              return (
                                                <div key={dayInfo.date} className="week-day">
                                                  <div className={`card h-100 ${dayInfo.isToday ? 'border-warning' : 'border-0'} ${shiftsOnDay.length === 0 ? 'bg-light' : 'shadow-sm'}`}>
                                                    <div className={`card-header p-2 text-center ${dayInfo.isToday ? 'bg-warning' : 'bg-light'}`}>
                                                      <div className="fw-bold" style={{ fontSize: '0.9rem' }}>
                                                        {dayInfo.dayName}
                                                      </div>
                                                      <div className="small">{dayInfo.dayNumber}</div>
                                                      {shiftsOnDay.length > 0 && (
                                                        <span className="badge bg-secondary mt-1" style={{ fontSize: '0.7rem' }}>
                                                          {totalHoursOnDay.toFixed(1)}h
                                                        </span>
                                                      )}
                                                    </div>
                                                    <div className="card-body p-2" style={{minHeight: '120px'}}>
                                                      {shiftsOnDay.length === 0 ? (
                                                        <div className="text-center text-muted small">
                                                          <i className="fas fa-calendar-day"></i>
                                                          <br />Frei
                                                        </div>
                                                      ) : (
                                                        sortedShifts.map((shift, shiftIndex) => {
                                                          // Finde tatsächliche Stunden für diese geplante Schicht
                                                          const actualHoursForShift = getActualHoursForPlannedShift(shift, employee.actual);
                                                          const plannedHours = calculateHours(shift.start_time, shift.end_time);
                                                          const actualHours = actualHoursForShift ? 
                                                            (actualHoursForShift.hours ?? calculateActualHours(actualHoursForShift.start_time, actualHoursForShift.end_time)) : null;
                                                          
                                                          return (
                                                            <div key={shiftIndex} className={`shift-overlay-container ${shiftIndex > 0 ? "mt-2 pt-2 border-top" : ""}`}>
                                                              {/* Station Name */}
                                                              <div className="text-center mb-1">
                                                                <div className="fw-bold text-primary" style={{ fontSize: '0.9rem', lineHeight: '1.1' }}>
                                                                  {getStationName(shift.station_id)}
                                                                </div>
                                                              </div>
                                                              
                                                              {/* Geplante Schicht (schwarz) */}
                                                              <div className="planned-shift-display text-center">
                                                                <div style={{ fontSize: '0.8rem', fontWeight: 'bold' }}>
                                                                  GEPLANT
                                                                </div>
                                                                <div style={{ fontSize: '0.8rem' }}>
                                                                  {shift.start_time}-{shift.end_time}
                                                                </div>
                                                                <div style={{ fontSize: '0.7rem' }}>
                                                                  {plannedHours.toFixed(1)}h
                                                                </div>
                                                              </div>
                                                              
                                                              {/* Tatsächliche Stunden (dunkelgrün) */}
                                                              {actualHoursForShift ? (
                                                                <div className="actual-hours-overlay text-center">
                                                                  <div style={{ fontSize: '0.75rem', fontWeight: 'bold' }}>
                                                                    IST
                                                                  </div>
                                                                  <div style={{ fontSize: '0.75rem' }}>
                                                                    {actualHoursForShift.start_time}-{actualHoursForShift.end_time}
                                                                  </div>
                                                                  {actualHoursForShift.pauses && actualHoursForShift.pauses.map((p) => <div key={p} style={{ fontSize: '0.65rem' }}>⏸ {p} Pause (unbezahlt)</div>)}
                                                                  <div className="hours-comparison">
                                                                    <span>{actualHours.toFixed(1)}h</span>
                                                                    {(() => {
                                                                      const diff = actualHours - plannedHours;
                                                                      const diffClass = diff > 0 ? 'positive' : diff < 0 ? 'negative' : 'neutral';
                                                                      const diffText = diff > 0 ? `+${diff.toFixed(1)}h` : `${diff.toFixed(1)}h`;
                                                                      return (
                                                                        <span className={`hours-difference ${diffClass}`}>
                                                                          {diffText}
                                                                        </span>
                                                                      );
                                                                    })()}
                                                                  </div>
                                                                </div>
                                                              ) : (
                                                                <div className="text-center mt-1">
                                                                  <small className="text-muted" style={{ fontSize: '0.65rem' }}>
                                                                    Keine Ist-Stunden erfasst
                                                                  </small>
                                                                </div>
                                                              )}
                                                              
                                                              {/* Kasse-Badge */}
                                                              {shift.is_cashier && (
                                                                <div className="text-center mt-1">
                                                                  <span className="badge bg-warning text-dark" style={{ fontSize: '0.6rem' }}>
                                                                    <i className="fas fa-cash-register me-1"></i>Kasse
                                                                  </span>
                                                                </div>
                                                              )}
                                                            </div>
                                                          );
                                                        })
                                                      )}
                                                    <ExtraActual entries={extraActualEntries(dayInfo.date, sortedShifts, employee.actual, getActualHoursForPlannedShift)} calculateHours={calculateHours} />                                                    </div>
                                                  </div>
                                                </div>
                                              );
                                            })}
                                          </div>
                                        </div>
                                      );
                                    })
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Admin Logs Section */}
          {user?.role === 'admin' && adminActiveTab === 'logs' && (
            <div className="logs-container">
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h3>
                  <i className="fas fa-table me-2 text-primary"></i>
                  Mitarbeiter-Übersicht & Stundenbestätigungen
                </h3>
                <button 
                  className="btn btn-primary btn-sm"
                  onClick={fetchEmployeeLogs}
                >
                  <i className="fas fa-sync-alt me-2"></i>Aktualisieren
                </button>
              </div>

              {/* Gesamtstunden Übersicht */}
              {employeeLogs.length > 0 && (
                <>
                  <div className="row mb-4">
                    {/* Gesamt Geplant */}
                    <div className="col-lg-3 col-md-6 mb-3">
                      <div className="card border-0 shadow-sm h-100 bg-light-success">
                        <div className="card-body text-center">
                          <i className="fas fa-calendar-check text-success mb-2" style={{fontSize: '2rem'}}></i>
                          <h6 className="card-title text-success">Geplant Gesamt</h6>
                          <h4 className="text-success mb-0">
                            {safeSumBackendHours(employeeLogs, 'total_planned_hours').toFixed(2)} Std
                          </h4>
                          <small className="text-muted">Alle Mitarbeiter</small>
                        </div>
                      </div>
                    </div>
                    
                    {/* September Geplant */}
                    <div className="col-lg-3 col-md-6 mb-3">
                      <div className="card border-0 shadow-sm h-100 bg-light-primary">
                        <div className="card-body text-center">
                          <i className="fas fa-calendar text-primary mb-2" style={{fontSize: '2rem'}}></i>
                          <h6 className="card-title text-primary">September Geplant</h6>
                          <h4 className="text-primary mb-0">
                            {safeSumBackendHours(employeeLogs, 'september_planned_hours').toFixed(2)} Std
                          </h4>
                          <small className="text-muted">Nur September</small>
                        </div>
                      </div>
                    </div>
                    
                    {/* Oktober Geplant */}
                    <div className="col-lg-3 col-md-6 mb-3">
                      <div className="card border-0 shadow-sm h-100 bg-light-warning">
                        <div className="card-body text-center">
                          <i className="fas fa-calendar text-warning mb-2" style={{fontSize: '2rem'}}></i>
                          <h6 className="card-title text-warning">Oktober Geplant</h6>
                          <h4 className="text-warning mb-0">
                            {safeSumBackendHours(employeeLogs, 'october_planned_hours').toFixed(2)} Std
                          </h4>
                          <small className="text-muted">Nur Oktober</small>
                        </div>
                      </div>
                    </div>
                    
                    {/* Gesamt Ist */}
                    <div className="col-lg-3 col-md-6 mb-3">
                      <div className="card border-0 shadow-sm h-100 bg-light-info">
                        <div className="card-body text-center">
                          <i className="fas fa-clock text-info mb-2" style={{fontSize: '2rem'}}></i>
                          <h6 className="card-title text-info">Ist Gesamt</h6>
                          <h4 className="text-info mb-0">
                            {safeSumBackendHours(employeeLogs, 'total_actual_hours').toFixed(2)} Std
                          </h4>
                          <small className="text-muted">Alle Mitarbeiter</small>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Zweite Reihe für Ist-Stunden nach Monaten */}
                  <div className="row mb-4">
                    <div className="col-lg-4"></div> {/* Leer für Zentrierung */}
                    
                    {/* September Ist */}
                    <div className="col-lg-2 col-md-6 mb-3">
                      <div className="card border-0 shadow-sm h-100 bg-light-secondary">
                        <div className="card-body text-center">
                          <i className="fas fa-clock text-secondary mb-2" style={{fontSize: '1.5rem'}}></i>
                          <h6 className="card-title text-secondary">Sep Ist</h6>
                          <h5 className="text-secondary mb-0">
                            {safeSumBackendHours(employeeLogs, 'september_actual_hours').toFixed(2)} Std
                          </h5>
                        </div>
                      </div>
                    </div>
                    
                    {/* Oktober Ist */}
                    <div className="col-lg-2 col-md-6 mb-3">
                      <div className="card border-0 shadow-sm h-100 bg-light-dark">
                        <div className="card-body text-center">
                          <i className="fas fa-clock text-dark mb-2" style={{fontSize: '1.5rem'}}></i>
                          <h6 className="card-title text-dark">Okt Ist</h6>
                          <h5 className="text-dark mb-0">
                            {safeSumBackendHours(employeeLogs, 'october_actual_hours').toFixed(2)} Std
                          </h5>
                        </div>
                      </div>
                    </div>
                    
                    <div className="col-lg-4"></div> {/* Leer für Zentrierung */}
                  </div>
                </>
              )}

              {/* Filter-Checkboxen */}
              {employeeLogs.length > 0 && (
                <div className="card mb-3">
                  <div className="card-header bg-light">
                    <h6 className="mb-0">
                      <i className="fas fa-filter me-2"></i>
                      Filter-Optionen
                    </h6>
                  </div>
                  <div className="card-body">
                    <div className="row">
                      <div className="col-md-6">
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="hideZeroPlannedHours"
                            checked={hideZeroPlannedHours}
                            onChange={(e) => handlePlannedHoursFilterChange(e.target.checked)}
                          />
                          <label className="form-check-label" htmlFor="hideZeroPlannedHours">
                            <i className="fas fa-calendar-check text-success me-2"></i>
                            Mitarbeiter ohne geplante Stunden ausblenden
                          </label>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="hideZeroActualHours"
                            checked={hideZeroActualHours}
                            onChange={(e) => handleActualHoursFilterChange(e.target.checked)}
                          />
                          <label className="form-check-label" htmlFor="hideZeroActualHours">
                            <i className="fas fa-clock text-info me-2"></i>
                            Mitarbeiter ohne tatsächliche Stunden ausblenden
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {loading ? (
                <div className="text-center p-4">
                  <div className="spinner-border text-primary" role="status">
                    <span className="sr-only">{t('reports.loading')}</span>
                  </div>
                </div>
              ) : (
                <div className="logs-content">
                  {employeeLogs.length === 0 ? (
                    <div className="alert alert-info">
                      <i className="fas fa-info-circle me-2"></i>
                      Keine aktiven Mitarbeiter gefunden.
                    </div>
                  ) : (
                    <>
                      <div className="mb-3">
                        <span className="badge bg-info me-2">
                          <i className="fas fa-users me-1"></i>
                          {employeeLogs.length} aktive Mitarbeiter
                        </span>
                        <small className="text-muted">
                          (Ohne "Abgesagt" oder "Keine Rückmeldung")
                        </small>
                      </div>
                      
                      <div className="table-responsive">
                        <table className="table table-striped table-hover table-sm" style={{fontSize: '1.35rem', marginBottom: 0, tableLayout: 'fixed'}}>
                          <thead className="table-dark">
                            <tr>
                              <th style={{color: '#ffffff', width: '12%', padding: '6px 4px', textAlign: 'left'}}>
                                <i className="fas fa-user me-1"></i>Mitarbeiter
                              </th>
                              <th style={{color: '#ffffff', width: '12%', padding: '6px 1px', textAlign: 'center'}}>
                                <i className="fas fa-calendar-check me-1"></i>Geplant Gesamt
                              </th>
                              <th style={{color: '#ffffff', width: '6%', padding: '6px 1px', textAlign: 'center'}}>
                                <i className="fas fa-calendar me-1"></i>Sep Geplant
                              </th>
                              <th style={{color: '#ffffff', width: '6%', padding: '6px 1px', textAlign: 'center'}}>
                                <i className="fas fa-calendar me-1"></i>Okt Geplant
                              </th>
                              <th style={{color: '#ffffff', width: '12%', padding: '6px 1px', textAlign: 'center'}}>
                                <i className="fas fa-clock me-1"></i>Ist Gesamt
                              </th>
                              <th style={{color: '#ffffff', width: '6%', padding: '6px 1px', textAlign: 'center'}}>
                                <i className="fas fa-clock me-1"></i>Sep Ist
                              </th>
                              <th style={{color: '#ffffff', width: '6%', padding: '6px 1px', textAlign: 'center'}}>
                                <i className="fas fa-clock me-1"></i>Okt Ist
                              </th>
                              <th style={{color: '#ffffff', width: '8%', padding: '6px 1px', textAlign: 'center'}}>
                                <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%'}}>
                                  <div style={{marginBottom: '3px', fontSize: '1.2rem'}}>
                                    <i className="fas fa-calendar-alt me-1"></i>Bereich 1
                                  </div>
                                  <div style={{display: 'flex', gap: '2px', alignItems: 'center', justifyContent: 'center', width: '100%'}}>
                                    <input
                                      type="date"
                                      value={customRange1Start}
                                      onChange={handleCustomRange1StartChange}
                                      style={{fontSize: '1.05rem', padding: '2px', width: '60px', backgroundColor: 'white', border: '1px solid #ccc'}}
                                    />
                                    <span style={{fontSize: '1.05rem', margin: '0 2px'}}>-</span>
                                    <input
                                      type="date"
                                      value={customRange1End}
                                      onChange={handleCustomRange1EndChange}
                                      style={{fontSize: '1.05rem', padding: '2px', width: '60px', backgroundColor: 'white', border: '1px solid #ccc'}}
                                    />
                                  </div>
                                </div>
                              </th>
                              <th style={{color: '#ffffff', width: '8%', padding: '6px 1px', textAlign: 'center'}}>
                                <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%'}}>
                                  <div style={{marginBottom: '3px', fontSize: '1.2rem'}}>
                                    <i className="fas fa-calendar-alt me-1"></i>Bereich 2
                                  </div>
                                  <div style={{display: 'flex', gap: '2px', alignItems: 'center', justifyContent: 'center', width: '100%'}}>
                                    <input
                                      type="date"
                                      value={customRange2Start}
                                      onChange={handleCustomRange2StartChange}
                                      style={{fontSize: '1.05rem', padding: '2px', width: '60px', backgroundColor: 'white', border: '1px solid #ccc'}}
                                    />
                                    <span style={{fontSize: '1.05rem', margin: '0 2px'}}>-</span>
                                    <input
                                      type="date"
                                      value={customRange2End}
                                      onChange={handleCustomRange2EndChange}
                                      style={{fontSize: '1.05rem', padding: '2px', width: '60px', backgroundColor: 'white', border: '1px solid #ccc'}}
                                    />
                                  </div>
                                </div>
                              </th>
                              <th style={{color: '#ffffff', width: '7%', padding: '6px 1px', textAlign: 'center'}}>
                                <i className="fas fa-traffic-light me-1"></i>Status
                              </th>
                              <th style={{color: '#ffffff', width: '11%', padding: '6px 1px', textAlign: 'center'}}>
                                <i className="fas fa-cog me-1"></i>Aktionen
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {employeeLogs
                              .filter(log => {
                                // Filter für geplante Stunden
                                if (hideZeroPlannedHours && (parseFloat(log.total_planned_hours) || 0) === 0) {
                                  return false;
                                }
                                // Filter für tatsächliche Stunden  
                                if (hideZeroActualHours && validateAndCorrectHours(log.total_actual_hours, `total_actual_hours for ${log.firstname} ${log.lastname}`) === 0) {
                                  return false;
                                }
                                return true;
                              })
                              .map((log, index) => (
                              <tr key={index} className={log.billing_hold ? 'billing-hold-row' : ''} style={log.billing_hold ? { textDecoration: 'line-through', opacity: 0.55, background: '#f3f3f3' } : undefined}
                                title={log.billing_hold ? `Noch NICHT abrechnen${log.billing_hold_note ? ': ' + log.billing_hold_note : ''}` : undefined}>
                                <td style={{verticalAlign: 'middle', padding: '8px 1px'}}>
                                  <div className="d-flex align-items-center">
                                    <div className="me-1" style={{minWidth: '16px'}}>
                                      <i className="fas fa-user-circle text-primary" style={{fontSize: '1rem'}}></i>
                                    </div>
                                    <div style={{minWidth: 0, flex: 1}}>
                                      <strong style={{display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '1.35rem'}}>
                                        {log.firstname} {log.lastname}
                                      </strong>
                                      <small className="text-muted" style={{fontSize: '1.1rem'}}>ID: {log.id}</small>
                                      {log.billing_hold && <div className="badge bg-dark" style={{ fontSize: '0.95rem', textDecoration: 'none', whiteSpace: 'normal', textAlign: 'left' }}>⛔ noch nicht abrechnen{log.billing_hold_note ? `: ${log.billing_hold_note}` : ''}</div>}
                                      {Array.isArray(log.absences) && log.absences.length > 0 && (
                                        <div className="badge bg-secondary" style={{ fontSize: '0.95rem', cursor: 'help', whiteSpace: 'normal', textAlign: 'left' }} title={log.absences.join('\n')}>🚫 {log.absences.length}× nicht erschienen/krank</div>
                                      )}
                                      {Array.isArray(log.issues) && log.issues.length > 0 && (
                                        <div className="badge bg-warning text-dark" style={{ fontSize: '0.95rem', cursor: 'help', whiteSpace: 'normal', textAlign: 'left' }} title={log.issues.join('\n')}>⚠️ {log.issues.length} Prüfhinweis{log.issues.length === 1 ? '' : 'e'}</div>
                                      )}
                                    </div>
                                  </div>
                                </td>
                                
                                {/* Geplant Gesamt */}
                                <td style={{verticalAlign: 'middle', padding: '8px 1px', textAlign: 'center'}}>
                                  <span className={`badge ${log.planned_confirmed ? 'bg-success' : 'bg-warning'}`} style={{fontSize: '1.2rem'}}>
                                    {log.total_planned_hours || 0} Std
                                  </span>
                                </td>
                                
                                {/* September Geplant */}
                                <td style={{verticalAlign: 'middle', padding: '8px 1px', textAlign: 'center'}}>
                                  <span className="badge bg-primary" style={{fontSize: '1.2rem'}}>
                                    {log.september_planned_hours || 0} Std
                                  </span>
                                </td>
                                
                                {/* Oktober Geplant */}
                                <td style={{verticalAlign: 'middle', padding: '8px 1px', textAlign: 'center'}}>
                                  <span className="badge bg-primary" style={{fontSize: '1.2rem'}}>
                                    {log.october_planned_hours || 0} Std
                                  </span>
                                </td>
                                
                                {/* Ist Gesamt */}
                                <td style={{verticalAlign: 'middle', padding: '8px 1px', textAlign: 'center'}}>
                                  <span className={`badge ${log.actual_confirmed ? 'bg-success' : 'bg-warning'}`} style={{fontSize: '1.2rem'}}>
                                    {validateAndCorrectHours(log.total_actual_hours, `total_actual_hours for ${log.firstname} ${log.lastname}`).toFixed(2)} Std
                                    {parseFloat(log.total_actual_hours || 0) < 0 && (
                                      <i className="fas fa-exclamation-triangle text-warning ms-1" 
                                         title="Frontend-Korrektur angewendet"></i>
                                    )}
                                  </span>
                                </td>
                                
                                {/* September Ist */}
                                <td style={{verticalAlign: 'middle', padding: '8px 1px', textAlign: 'center'}}>
                                  <span className="badge bg-info" style={{fontSize: '1.2rem'}}>
                                    {validateAndCorrectHours(log.september_actual_hours, `september_actual_hours for ${log.firstname} ${log.lastname}`).toFixed(2)} Std
                                    {parseFloat(log.september_actual_hours || 0) < 0 && (
                                      <i className="fas fa-exclamation-triangle text-warning ms-1" 
                                         title="Frontend-Korrektur angewendet"></i>
                                    )}
                                  </span>
                                </td>
                                
                                {/* Oktober Ist */}
                                <td style={{verticalAlign: 'middle', padding: '8px 1px', textAlign: 'center'}}>
                                  <span className="badge bg-info" style={{fontSize: '1.2rem'}}>
                                    {validateAndCorrectHours(log.october_actual_hours, `october_actual_hours for ${log.firstname} ${log.lastname}`).toFixed(2)} Std
                                    {parseFloat(log.october_actual_hours || 0) < 0 && (
                                      <i className="fas fa-exclamation-triangle text-warning ms-1" 
                                         title="Frontend-Korrektur angewendet"></i>
                                    )}
                                  </span>
                                </td>

                                {/* Custom Range 1 */}
                                <td style={{verticalAlign: 'middle', padding: '8px 1px', textAlign: 'center'}}>
                                  <span className="badge bg-secondary" style={{fontSize: '1.2rem'}}>
                                    {customRangeHours[log.id]?.range1?.toFixed(2) || '0.00'} Std
                                  </span>
                                  {customRange1Start && customRange1End && (
                                    <div style={{fontSize: '0.95rem', color: '#666', marginTop: '1px'}}>
                                      {formatDateForDisplay(customRange1Start)} - {formatDateForDisplay(customRange1End)}
                                    </div>
                                  )}
                                </td>

                                {/* Custom Range 2 */}
                                <td style={{verticalAlign: 'middle', padding: '8px 1px', textAlign: 'center'}}>
                                  <span className="badge bg-secondary" style={{fontSize: '1.2rem'}}>
                                    {customRangeHours[log.id]?.range2?.toFixed(2) || '0.00'} Std
                                  </span>
                                  {customRange2Start && customRange2End && (
                                    <div style={{fontSize: '0.95rem', color: '#666', marginTop: '1px'}}>
                                      {formatDateForDisplay(customRange2Start)} - {formatDateForDisplay(customRange2End)}
                                    </div>
                                  )}
                                </td>
                                
                                {/* Status */}
                                <td style={{verticalAlign: 'middle', padding: '8px 1px', textAlign: 'center'}}>
                                  {log.planned_confirmed && log.actual_confirmed ? (
                                    <span className="badge bg-success" style={{whiteSpace: 'nowrap', fontSize: '1.1rem'}}>
                                      <i className="fas fa-check-double me-1"></i>Vollständig
                                    </span>
                                  ) : log.planned_confirmed || log.actual_confirmed ? (
                                    <span className="badge bg-warning" style={{whiteSpace: 'nowrap', fontSize: '1.1rem'}}>
                                      <i className="fas fa-exclamation-triangle me-1"></i>Teilweise
                                    </span>
                                  ) : (
                                    <span className="badge bg-danger" style={{whiteSpace: 'nowrap', fontSize: '1.1rem'}}>
                                      <i className="fas fa-times me-1"></i>Ausstehend
                                    </span>
                                  )}
                                </td>
                                
                                {/* Aktionen */}
                                <td style={{verticalAlign: 'middle', padding: '8px 1px', textAlign: 'center'}}>
                                  <div className="btn-group" role="group" style={{display: 'flex', gap: '2px', justifyContent: 'center', flexWrap: 'wrap'}}>
                                    {/* Bestätigen Buttons - Erste Reihe */}
                                    <div style={{display: 'flex', gap: '2px', marginBottom: '2px'}}>
                                      <button
                                        className="btn btn-outline-success btn-sm"
                                        onClick={() => confirmEmployeeHours(log.id, 'planned')}
                                        disabled={log.planned_confirmed}
                                        title="Geplante Stunden bestätigen"
                                        style={{width: '35px', height: '35px', padding: '0', display: 'flex', alignItems: 'center', justifyContent: 'center'}}
                                      >
                                        <i className="fas fa-calendar-check" style={{fontSize: '1rem'}}></i>
                                      </button>
                                      <button
                                        className="btn btn-outline-info btn-sm"
                                        onClick={() => confirmEmployeeHours(log.id, 'actual')}
                                        disabled={log.actual_confirmed}
                                        title="Ist-Stunden bestätigen"
                                        style={{width: '35px', height: '35px', padding: '0', display: 'flex', alignItems: 'center', justifyContent: 'center'}}
                                      >
                                        <i className="fas fa-clock" style={{fontSize: '1rem'}}></i>
                                      </button>
                                      <button
                                        className="btn btn-outline-secondary btn-sm"
                                        onClick={() => setSelectedEmployee(log.id)}
                                        title="Details anzeigen"
                                        style={{width: '35px', height: '35px', padding: '0', display: 'flex', alignItems: 'center', justifyContent: 'center'}}
                                      >
                                        <i className="fas fa-eye" style={{fontSize: '1rem'}}></i>
                                      </button>
                                    </div>
                                    {/* Zurücksetzen Buttons - Zweite Reihe */}
                                    <div style={{display: 'flex', gap: '2px'}}>
                                      <button
                                        className={`btn btn-sm ${log.billing_hold ? 'btn-dark' : 'btn-outline-dark'}`}
                                        onClick={() => toggleBillingHold(log)}
                                        title={log.billing_hold ? 'Wieder zur Abrechnung freigeben' : 'Bitte noch nicht abrechnen (Zeile wird durchgestrichen)'}
                                        style={{width: '35px', height: '35px', padding: '0', display: 'flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none'}}
                                      >
                                        {log.billing_hold ? '↩' : '⛔'}
                                      </button>
                                      <button
                                        className="btn btn-outline-warning btn-sm"
                                        onClick={() => unconfirmEmployeeHours(log.id, 'planned')}
                                        disabled={!log.planned_confirmed}
                                        title="Geplante Stunden zurücksetzen"
                                        style={{width: '35px', height: '35px', padding: '0', display: 'flex', alignItems: 'center', justifyContent: 'center'}}
                                      >
                                        <i className="fas fa-calendar-times" style={{fontSize: '1rem'}}></i>
                                      </button>
                                      <button
                                        className="btn btn-outline-danger btn-sm"
                                        onClick={() => unconfirmEmployeeHours(log.id, 'actual')}
                                        disabled={!log.actual_confirmed}
                                        title="Ist-Stunden zurücksetzen"
                                        style={{width: '35px', height: '35px', padding: '0', display: 'flex', alignItems: 'center', justifyContent: 'center'}}
                                      >
                                        <i className="fas fa-times-circle" style={{fontSize: '1rem'}}></i>
                                      </button>
                                      <button
                                        className="btn btn-outline-primary btn-sm"
                                        onClick={() => window.open(`/employee-details/${log.id}`, '_blank')}
                                        title="In neuem Tab öffnen"
                                        style={{width: '35px', height: '35px', padding: '0', display: 'flex', alignItems: 'center', justifyContent: 'center'}}
                                      >
                                        <i className="fas fa-external-link-alt" style={{fontSize: '1rem'}}></i>
                                      </button>
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {!selectedEmployee && (!multiSelectMode || selectedEmployees.length === 0) && adminActiveTab === 'reports' && (
            <div className="alert alert-info">
              <i className="fas fa-info-circle me-2"></i>
              {user?.role === 'admin' 
                ? 'Bitte wählen Sie einen Mitarbeiter aus, um dessen Einsatzzeiten anzuzeigen.'
                : 'Ihre persönlichen Einsatzzeiten werden geladen...'}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const PlannedShiftsView = ({ shifts, sortMonthsChronologically, formatDate, calculateHours }) => {
  // Verwende die neue Wochen-Gruppierung
  const groupByWeeks = (data) => {
    const weeks = {};
    
    data.forEach(entry => {
      const date = new Date(entry.date);
      
      // Finde den Montag dieser Woche
      const dayOfWeek = date.getDay(); // 0=Sonntag, 1=Montag, ..., 6=Samstag
      const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Korrigiere für Sonntag
      const monday = new Date(date);
      monday.setDate(date.getDate() - daysToMonday);
      
      // Erstelle Wochen-Key (YYYY-MM-DD des Montags)
      const weekKey = monday.toISOString().split('T')[0];
      
      if (!weeks[weekKey]) {
        weeks[weekKey] = {
          monday: monday,
          days: {}
        };
      }
      
      const dayKey = entry.date;
      if (!weeks[weekKey].days[dayKey]) {
        weeks[weekKey].days[dayKey] = [];
      }
      weeks[weekKey].days[dayKey].push(entry);
    });
    
    return weeks;
  };

  // Hilfsfunktion: Erstelle alle 7 Tage einer Woche
  const getWeekDays = (mondayDate) => {
    const days = [];
    const dayNames = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
    
    for (let i = 0; i < 7; i++) {
      const day = new Date(mondayDate);
      day.setDate(mondayDate.getDate() + i);
      days.push({
        date: day.toISOString().split('T')[0],
        dayName: dayNames[i],
        dayNumber: day.getDate(),
        isToday: day.toDateString() === new Date().toDateString()
      });
    }
    return days;
  };

  const groupedShifts = groupByWeeks(shifts);

  if (shifts.length === 0) {
    return (
      <div className="text-center py-4">
        <i className="fas fa-calendar-times text-muted mb-3" style={{fontSize: '3rem'}}></i>
        <h5 className="text-muted mb-2">Keine geplanten Schichten gefunden</h5>
        <p className="text-muted">
          Für diesen Mitarbeiter wurden noch keine Schichten zugewiesen.<br/>
          Schichten können in der <strong>Personalplanung</strong> oder <strong>KI-Planung</strong> zugewiesen werden.
        </p>
      </div>
    );
  }

  return (
    <div>
      {/* Kalender-Export Info */}
      {shifts.length > 0 && (
        <div className="alert alert-success mb-4" style={{
          backgroundColor: '#d4edda',
          borderColor: '#c3e6cb',
          color: '#155724'
        }}>
          <div className="d-flex align-items-start">
            <i className="fas fa-mobile-alt me-3 mt-1" style={{color: '#28a745', fontSize: '1.2rem'}}></i>
            <div>
              <strong style={{color: '#155724'}}>📱 Mobile Kalender-Export verfügbar!</strong>
              <p className="mb-2 mt-1" style={{color: '#155724'}}>
                Exportieren Sie Ihre geplanten Schichten als Kalender-Datei (.ics) und importieren Sie diese in:
              </p>
              <ul className="mb-2" style={{color: '#155724'}}>
                <li><strong>iPhone:</strong> Öffnen Sie die .ics-Datei → "Zu Kalender hinzufügen"</li>
                <li><strong>Android:</strong> Öffnen Sie die .ics-Datei mit Google Kalender oder Samsung Kalender</li>
                <li><strong>PC/Mac:</strong> Importieren Sie in Outlook, Thunderbird oder andere Kalender-Apps</li>
              </ul>
              <small style={{color: '#0f5132'}}>
                <i className="fas fa-calendar-download me-1"></i>
                Nutzen Sie den <strong>"📱 Kalender Export"</strong> Button oben rechts.
              </small>
            </div>
          </div>
        </div>
      )}
      
      {Object.entries(groupedShifts)
        .sort(([weekA], [weekB]) => new Date(weekA) - new Date(weekB))
        .map(([weekKey, weekData]) => {
          const weekDays = getWeekDays(weekData.monday);
          const totalShiftsInWeek = Object.values(weekData.days).flat().length;
          const totalHoursInWeek = Object.values(weekData.days).flat()
            .reduce((sum, shift) => sum + calculateHours(shift.start_time, shift.end_time), 0);
          
          // Formatiere Wochenbeschreibung
          const startDate = weekData.monday.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
          const endDate = new Date(weekData.monday.getTime() + 6 * 24 * 60 * 60 * 1000)
            .toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });

          return (
            <div key={weekKey} className="mb-4">
              <h5 className="text-primary mb-3">
                <i className="fas fa-calendar-week me-2"></i>
                Woche {startDate} - {endDate}
                <span className="badge bg-primary ms-2">{totalShiftsInWeek} Schichten</span>
                <span className="badge bg-success ms-1">{totalHoursInWeek.toFixed(1)}h</span>
              </h5>
              
              <div className="week-view">
                {weekDays.map((dayInfo) => {
                  const shiftsOnDay = weekData.days[dayInfo.date] || [];
                  const sortedShifts = shiftsOnDay.sort((a, b) => 
                    a.start_time.localeCompare(b.start_time)
                  );
                  
                  // Berechne Gesamtstunden für diesen Tag
                  const totalHoursOnDay = sortedShifts.reduce((sum, shift) => 
                    sum + calculateHours(shift.start_time, shift.end_time), 0
                  );

                  return (
                    <div key={dayInfo.date} className="week-day">
                      <div className={`card h-100 ${dayInfo.isToday ? 'border-warning' : 'border-0'} ${shiftsOnDay.length === 0 ? 'bg-light' : 'shadow-sm'}`}>
                        <div className={`card-header p-2 text-center ${dayInfo.isToday ? 'bg-warning' : 'bg-light'}`}>
                          <div className="fw-bold" style={{ fontSize: '0.9rem' }}>
                            {dayInfo.dayName}
                          </div>
                          <div className="small">{dayInfo.dayNumber}</div>
                          {shiftsOnDay.length > 0 && (
                            <span className="badge bg-secondary mt-1" style={{ fontSize: '0.7rem' }}>
                              {totalHoursOnDay.toFixed(1)}h
                            </span>
                          )}
                        </div>
                        <div className="card-body p-2" style={{minHeight: '120px'}}>
                          {shiftsOnDay.length === 0 ? (
                            <div className="text-center text-muted small">
                              <i className="fas fa-calendar-day"></i>
                              <br />Frei
                            </div>
                          ) : (
                            sortedShifts.map((shift, shiftIndex) => (
                              <div key={shiftIndex} className={shiftIndex > 0 ? "mt-2 pt-2 border-top" : ""}>
                                <div className="text-center mb-1">
                                  <div className="fw-bold text-primary" style={{ fontSize: '0.9rem', lineHeight: '1.1' }}>
                                    {getStationName(shift.station_id)}
                                  </div>
                                </div>
                                <div className="text-center mb-1">
                                  <small className="text-dark fw-semibold" style={{ fontSize: '0.8rem' }}>
                                    {shift.start_time}-{shift.end_time}
                                  </small>
                                  <div>
                                    <span className="badge bg-info" style={{ fontSize: '0.6rem' }}>
                                      {calculateHours(shift.start_time, shift.end_time).toFixed(1)}h
                                    </span>
                                  </div>
                                </div>
                                {shift.is_cashier && (
                                  <div className="text-center">
                                    <span className="badge bg-warning text-dark" style={{ fontSize: '0.6rem' }}>
                                      <i className="fas fa-cash-register me-1"></i>Kasse
                                    </span>
                                  </div>
                                )}
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
    </div>
  );
};

const ActualHoursView = ({ hours, sortMonthsChronologically, formatDate, calculateHours }) => {
  // Verwende die neue Wochen-Gruppierung
  const groupByWeeks = (data) => {
    const weeks = {};
    
    data.forEach(entry => {
      const date = new Date(entry.date);
      
      // Finde den Montag dieser Woche
      const dayOfWeek = date.getDay(); // 0=Sonntag, 1=Montag, ..., 6=Samstag
      const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Korrigiere für Sonntag
      const monday = new Date(date);
      monday.setDate(date.getDate() - daysToMonday);
      
      // Erstelle Wochen-Key (YYYY-MM-DD des Montags)
      const weekKey = monday.toISOString().split('T')[0];
      
      if (!weeks[weekKey]) {
        weeks[weekKey] = {
          monday: monday,
          days: {}
        };
      }
      
      const dayKey = entry.date;
      if (!weeks[weekKey].days[dayKey]) {
        weeks[weekKey].days[dayKey] = [];
      }
      weeks[weekKey].days[dayKey].push(entry);
    });
    
    return weeks;
  };

  // Hilfsfunktion: Erstelle alle 7 Tage einer Woche
  const getWeekDays = (mondayDate) => {
    const days = [];
    const dayNames = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
    
    for (let i = 0; i < 7; i++) {
      const day = new Date(mondayDate);
      day.setDate(mondayDate.getDate() + i);
      days.push({
        date: day.toISOString().split('T')[0],
        dayName: dayNames[i],
        dayNumber: day.getDate(),
        isToday: day.toDateString() === new Date().toDateString()
      });
    }
    return days;
  };

  const groupedHours = groupByWeeks(hours);

  if (hours.length === 0) {
    return (
      <div className="text-center py-4">
        <i className="fas fa-clock-rotate-left text-muted mb-3" style={{fontSize: '3rem'}}></i>
        <h5 className="text-muted mb-2">Keine tatsächlich geleisteten Stunden erfasst</h5>
        <p className="text-muted">
          Für diesen Mitarbeiter wurden noch keine Arbeitszeiten erfasst.<br/>
          Ist-Stunden können im <strong>Ist-Stunden</strong> Bereich eingetragen werden.
        </p>
      </div>
    );
  }

  return (
    <div>
      {Object.entries(groupedHours)
        .sort(([weekA], [weekB]) => new Date(weekA) - new Date(weekB))
        .map(([weekKey, weekData]) => {
          const weekDays = getWeekDays(weekData.monday);
          const totalEntriesInWeek = Object.values(weekData.days).flat().length;
          const totalHoursInWeek = Object.values(weekData.days).flat()
            .reduce((sum, entry) => sum + calculateHours(entry.start_time, entry.end_time), 0);
          
          // Formatiere Wochenbeschreibung
          const startDate = weekData.monday.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
          const endDate = new Date(weekData.monday.getTime() + 6 * 24 * 60 * 60 * 1000)
            .toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });

          return (
            <div key={weekKey} className="mb-4">
              <h5 className="text-success mb-3">
                <i className="fas fa-calendar-check me-2"></i>
                Woche {startDate} - {endDate}
                <span className="badge bg-success ms-2">{totalEntriesInWeek} Einträge</span>
                <span className="badge bg-info ms-1">{totalHoursInWeek.toFixed(1)}h</span>
              </h5>
              
              <div className="week-view">
                {weekDays.map((dayInfo) => {
                  const entriesOnDay = weekData.days[dayInfo.date] || [];
                  const sortedEntries = entriesOnDay.sort((a, b) => 
                    a.start_time.localeCompare(b.start_time)
                  );
                  
                  // Berechne Gesamtstunden für diesen Tag
                  const totalHoursOnDay = sortedEntries.reduce((sum, entry) => 
                    sum + calculateHours(entry.start_time, entry.end_time), 0
                  );

                  return (
                    <div key={dayInfo.date} className="week-day">
                      <div className={`card h-100 ${dayInfo.isToday ? 'border-warning' : 'border-0'} ${entriesOnDay.length === 0 ? 'bg-light' : 'shadow-sm'}`}>
                        <div className={`card-header p-2 text-center ${dayInfo.isToday ? 'bg-warning' : 'bg-light'}`}>
                          <div className="fw-bold" style={{ fontSize: '0.9rem' }}>
                            {dayInfo.dayName}
                          </div>
                          <div className="small">{dayInfo.dayNumber}</div>
                          {entriesOnDay.length > 0 && (
                            <span className="badge bg-secondary mt-1" style={{ fontSize: '0.7rem' }}>
                              {totalHoursOnDay.toFixed(1)}h
                            </span>
                          )}
                        </div>
                        <div className="card-body p-2" style={{minHeight: '120px'}}>
                          {entriesOnDay.length === 0 ? (
                            <div className="text-center text-muted small">
                              <i className="fas fa-calendar-day"></i>
                              <br />Frei
                            </div>
                          ) : (
                            sortedEntries.map((entry, entryIndex) => (
                              <div key={entryIndex} className={entryIndex > 0 ? "mt-2 pt-2 border-top" : ""}>
                                <div className="text-center mb-1">
                                  <div className="fw-bold text-success" style={{ fontSize: '0.9rem', lineHeight: '1.1' }}>
                                    {getStationName(entry.station_id)}
                                  </div>
                                </div>
                                <div className="text-center mb-1">
                                  <small className="text-dark fw-semibold" style={{ fontSize: '0.8rem' }}>
                                    {entry.start_time}-{entry.end_time}
                                  </small>
                                  <div>
                                    <span className="badge bg-info" style={{ fontSize: '0.6rem' }}>
                                      {calculateHours(entry.start_time, entry.end_time).toFixed(1)}h
                                    </span>
                                  </div>
                                </div>
                                <div className="text-center">
                                  <small className="text-muted" style={{ fontSize: '0.7rem' }}>
                                    Plan: {entry.planned_start}-{entry.planned_end}
                                  </small>
                                </div>
                                {entry.notes && (
                                  <div className="text-center mt-1">
                                    <small className="text-info" title={entry.notes}>
                                      <i className="fas fa-sticky-note"></i>
                                    </small>
                                  </div>
                                )}
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
    </div>
  );
};


// Ist-Einträge eines Tages, die KEINER geplanten Schicht zugeordnet werden (andere Station, doppelter Eintrag).
// Sie zählen in die Gesamtsumme und müssen deshalb sichtbar sein – sonst sucht man die Differenz vergeblich.
const extraActualEntries = (date, sortedShifts, actualList, matchFn) => {
  if (!Array.isArray(actualList)) return [];
  const matched = new Set(sortedShifts.map((sh) => matchFn(sh, actualList)).filter(Boolean).flatMap((m) => m.segments || [m]));
  const toMin = (t) => { const [h, m] = String(t || '0:0').split(':').map(Number); return h * 60 + (m || 0); };
  const overlaps = (a, b) => { let a1 = toMin(a.start_time), a2 = toMin(a.end_time), b1 = toMin(b.start_time), b2 = toMin(b.end_time); if (a2 <= a1) a2 += 1440; if (b2 <= b1) b2 += 1440; return Math.min(a2, b2) - Math.max(a1, b1) > 0; };
  return actualList.filter((a) => a.date === date && !matched.has(a)).map((a) => ({ ...a, overlap: [...matched].some((m) => overlaps(a, m)) }));
};
const ExtraActual = ({ entries, calculateHours }) => entries.map((a, i) => (
  <div key={`x${i}`} className="mt-2 pt-2 border-top text-center" title="Dieser Ist-Eintrag gehört zu keiner geplanten Schicht an diesem Tag – zählt aber in die Gesamtsumme">
    <div className="fw-bold" style={{ fontSize: '0.85rem', color: '#b45309' }}>{getStationName(a.station_id)}</div>
    <div className="rounded px-1" style={{ background: '#fff3cd', border: '1px solid #f0ad4e', fontSize: '0.75rem' }}>
      <div className="fw-bold">IST ohne Plan</div>
      <div>{a.start_time}-{a.end_time} · {calculateHours(a.start_time, a.end_time).toFixed(1)}h</div>
      {a.overlap && <div style={{ fontSize: '0.65rem' }}>⚠️ überschneidet sich – doppelt erfasst?</div>}
    </div>
  </div>
));

// Kombinierte Ansicht für geplante und tatsächliche Stunden
const CombinedView = ({ 
  plannedShifts, 
  actualHours, 
  sortMonthsChronologically, 
  formatDate, 
  calculateHours,
  getActualHoursForPlannedShift,
  calculateActualHours,
  getStationName
}) => {
  // Verwende die gleiche Wochen-Gruppierung wie in der Mehrfachauswahl
  const groupByWeeks = (data) => {
    const weeks = {};
    
    data.forEach(entry => {
      const date = new Date(entry.date);
      
      // Finde den Montag dieser Woche
      const dayOfWeek = date.getDay(); // 0=Sonntag, 1=Montag, ..., 6=Samstag
      const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Korrigiere für Sonntag
      const monday = new Date(date);
      monday.setDate(date.getDate() - daysToMonday);
      
      // Erstelle Wochen-Key (YYYY-MM-DD des Montags)
      const weekKey = monday.toISOString().split('T')[0];
      
      if (!weeks[weekKey]) {
        weeks[weekKey] = {
          monday: monday,
          days: {}
        };
      }
      
      const dayKey = entry.date;
      if (!weeks[weekKey].days[dayKey]) {
        weeks[weekKey].days[dayKey] = [];
      }
      weeks[weekKey].days[dayKey].push(entry);
    });
    
    return weeks;
  };

  // Hilfsfunktion: Erstelle alle 7 Tage einer Woche
  const getWeekDays = (mondayDate) => {
    const days = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(mondayDate);
      day.setDate(mondayDate.getDate() + i);
      days.push({
        date: day.toISOString().split('T')[0],
        dayName: day.toLocaleDateString('de-DE', { weekday: 'short' }),
        dayNumber: day.getDate(),
        isToday: day.toDateString() === new Date().toDateString()
      });
    }
    return days;
  };

  if (!plannedShifts || plannedShifts.length === 0) {
    return (
      <div className="text-center text-muted py-5">
        <i className="fas fa-calendar-times fa-3x mb-3"></i>
        <h5>Keine geplanten Schichten</h5>
        <p>Für den ausgewählten Zeitraum wurden noch keine Schichten geplant.</p>
      </div>
    );
  }

  const groupedShifts = groupByWeeks(plannedShifts);

  return (
    <div>
      {Object.keys(groupedShifts).length === 0 ? (
        <div className="text-center text-muted py-3">
          <i className="fas fa-calendar-times fa-2x mb-2"></i>
          <p>Keine geplanten Schichten für dieses Jahr</p>
        </div>
      ) : (
        Object.entries(groupedShifts)
          .sort(([weekA], [weekB]) => new Date(weekA) - new Date(weekB))
          .map(([weekKey, weekData]) => {
            const weekDays = getWeekDays(weekData.monday);
            const totalShiftsInWeek = Object.values(weekData.days).flat().length;
            const totalHoursInWeek = Object.values(weekData.days).flat()
              .reduce((sum, shift) => sum + calculateHours(shift.start_time, shift.end_time), 0);
            
            // Formatiere Wochenbeschreibung
            const startDate = weekData.monday.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
            const endDate = new Date(weekData.monday.getTime() + 6 * 24 * 60 * 60 * 1000)
              .toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });

            return (
              <div key={weekKey} className="mb-4">
                <h6 className="text-primary border-bottom pb-2 mb-3">
                  <i className="fas fa-calendar-week me-1"></i>
                  Woche {startDate} - {endDate}
                  <span className="badge bg-primary ms-2">{totalShiftsInWeek} Schichten</span>
                  <span className="badge bg-success ms-1">{totalHoursInWeek.toFixed(1)}h geplant</span>
                </h6>
                <div className="week-view">
                  {weekDays.map((dayInfo) => {
                    const shiftsOnDay = weekData.days[dayInfo.date] || [];
                    const sortedShifts = shiftsOnDay.sort((a, b) => 
                      a.start_time.localeCompare(b.start_time)
                    );
                    
                    // Berechne Gesamtstunden für diesen Tag
                    const totalHoursOnDay = sortedShifts.reduce((sum, shift) => 
                      sum + calculateHours(shift.start_time, shift.end_time), 0
                    );

                    return (
                      <div key={dayInfo.date} className="week-day">
                        <div className={`card h-100 ${dayInfo.isToday ? 'border-warning' : 'border-0'} ${shiftsOnDay.length === 0 ? 'bg-light' : 'shadow-sm'}`}>
                          <div className={`card-header p-2 text-center ${dayInfo.isToday ? 'bg-warning' : 'bg-light'}`}>
                            <div className="fw-bold" style={{ fontSize: '0.9rem' }}>
                              {dayInfo.dayName}
                            </div>
                            <div className="small">{dayInfo.dayNumber}</div>
                            {shiftsOnDay.length > 0 && (
                              <span className="badge bg-secondary mt-1" style={{ fontSize: '0.7rem' }}>
                                {totalHoursOnDay.toFixed(1)}h
                              </span>
                            )}
                          </div>
                          <div className="card-body p-2" style={{minHeight: '120px'}}>
                            {shiftsOnDay.length === 0 ? (
                              <div className="text-center text-muted small">
                                <i className="fas fa-calendar-day"></i>
                                <br />Frei
                              </div>
                            ) : (
                              sortedShifts.map((shift, shiftIndex) => {
                                // Finde tatsächliche Stunden für diese geplante Schicht
                                const actualHoursForShift = getActualHoursForPlannedShift(shift, actualHours);
                                const plannedHours = calculateHours(shift.start_time, shift.end_time);
                                const actualWorkedHours = actualHoursForShift ? 
                                  (actualHoursForShift.hours ?? calculateActualHours(actualHoursForShift.start_time, actualHoursForShift.end_time)) : null;
                                
                                return (
                                  <div key={shiftIndex} className={`shift-overlay-container ${shiftIndex > 0 ? "mt-2 pt-2 border-top" : ""}`}>
                                    {/* Station Name */}
                                    <div className="text-center mb-1">
                                      <div className="fw-bold text-primary" style={{ fontSize: '0.9rem', lineHeight: '1.1' }}>
                                        {getStationName(shift.station_id)}
                                      </div>
                                    </div>
                                    
                                    {/* Geplante Schicht (schwarz) */}
                                    <div className="planned-shift-display text-center">
                                      <div style={{ fontSize: '0.8rem', fontWeight: 'bold' }}>
                                        GEPLANT
                                      </div>
                                      <div style={{ fontSize: '0.8rem' }}>
                                        {shift.start_time}-{shift.end_time}
                                      </div>
                                      <div style={{ fontSize: '0.7rem' }}>
                                        {plannedHours.toFixed(1)}h
                                      </div>
                                    </div>
                                    
                                    {/* Tatsächliche Stunden (dunkelgrün) */}
                                    {actualHoursForShift ? (
                                      <div className="actual-hours-overlay text-center">
                                        <div style={{ fontSize: '0.75rem', fontWeight: 'bold' }}>
                                          IST
                                        </div>
                                        <div style={{ fontSize: '0.75rem' }}>
                                          {actualHoursForShift.start_time}-{actualHoursForShift.end_time}
                                        </div>
                                        {actualHoursForShift.pauses && actualHoursForShift.pauses.map((p) => <div key={p} style={{ fontSize: '0.65rem' }}>⏸ {p} Pause (unbezahlt)</div>)}
                                        <div className="hours-comparison">
                                          <span>{actualWorkedHours.toFixed(1)}h</span>
                                          {(() => {
                                            const diff = actualWorkedHours - plannedHours;
                                            const diffClass = diff > 0 ? 'positive' : diff < 0 ? 'negative' : 'neutral';
                                            const diffText = diff > 0 ? `+${diff.toFixed(1)}h` : `${diff.toFixed(1)}h`;
                                            return (
                                              <span className={`hours-difference ${diffClass}`}>
                                                {diffText}
                                              </span>
                                            );
                                          })()}
                                        </div>
                                      </div>
                                    ) : (
                                      <div className="text-center mt-1">
                                        <small className="text-muted" style={{ fontSize: '0.65rem' }}>
                                          Keine Ist-Stunden erfasst
                                        </small>
                                      </div>
                                    )}
                                    
                                    {/* Kasse-Badge */}
                                    {shift.is_cashier && (
                                      <div className="text-center mt-1">
                                        <span className="badge bg-warning text-dark" style={{ fontSize: '0.6rem' }}>
                                          <i className="fas fa-cash-register me-1"></i>Kasse
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                );
                              })
                            )}
                          <ExtraActual entries={extraActualEntries(dayInfo.date, sortedShifts, actualHours, getActualHoursForPlannedShift)} calculateHours={calculateHours} />                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })
      )}
    </div>
  );
};

export default EmployeeWorkReports;
